import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AcademicCapIcon,
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightOnRectangleIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  FunnelIcon,
  HomeIcon,
  KeyIcon,
  LockClosedIcon,
  LockOpenIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PhoneIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TrashIcon,
  UserGroupIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from '../components/common/NotificationBell';
import userService from '../services/userService';
import { createClassroom, getClassrooms } from '../services/trainingService';
import { generateRandomClassCode, getInitialClassDates, calculateEndDate } from '../utils/classroomUtils';
import { buildAccountNotifications } from '../utils/notificationUtils';
import './AccountManagement.css';

const QUANTITY_PRESETS = [5, 10, 20, 50, 100];
const QUOTA_PRESETS = [5, 10, 20, 50];
const ENABLE_CLASSROOM_FIELDS = false;

const getTodayPlusMonths = (months) => {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().split('T')[0];
};

const formatDate = (value) => {
  if (!value) return 'Không giới hạn';
  return new Intl.DateTimeFormat('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(value));
};

const formatShortDate = (value) => {
  if (!value) return 'Không giới hạn';
  return new Intl.DateTimeFormat('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(value));
};

const formatDateTime = (value) => {
  if (!value) return '';
  return new Intl.DateTimeFormat('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const formatHeaderDateTime = () => {
  const now = new Date();
  const days = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
  const dayName = days[now.getDay()];
  const dateStr = now.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timeStr = now.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
  return `${dayName}, ${dateStr} | ${timeStr}`;
};

const daysLeft = (value) => {
  if (!value) return null;
  const diff = new Date(value).getTime() - Date.now();
  return Math.ceil(diff / 86400000);
};

const getDaysLeftClass = (value) => {
  const left = daysLeft(value);
  if (left === null) return 'unlimited';
  if (left < 0) return 'expired';
  if (left <= 7) return 'warning';
  return 'ok';
};

const getStatus = (account) => account.effective_account_status || account.account_status;

const statusLabel = (account) => {
  const status = getStatus(account);
  if (status === 'locked') return 'Đã khóa';
  if (account.is_classroom_expired) return 'Lớp đã kết thúc';
  if (status === 'expired') return 'Hết hạn ngày';
  if (status === 'quota_exceeded') return 'Hết lượt thi';
  const left = daysLeft(account.expires_at);
  if (left !== null && left <= 7) return 'Sắp hết hạn';
  return 'Đang hoạt động';
};


const isAdminRole = (role) => ['admin', 'administrator'].includes((role || '').toLowerCase());

const roleLabel = (role) => {
  if (isAdminRole(role)) return 'Quản trị viên';
  if (role === 'center') return 'Trung tâm';
  return 'Học viên';
};

const classroomMatchesCenter = (classroom, centerId) => {
  if (!centerId) return true;
  return !classroom?.center_id || String(classroom.center_id) === String(centerId);
};

const getInitials = (name, email) => {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length > 0) {
      return parts[parts.length - 1].charAt(0).toUpperCase();
    }
  }
  if (email) return email.charAt(0).toUpperCase();
  return 'U';
};

const AVATAR_PALETTE = [
  { bg: '#eff6ff', color: '#2563eb' },
  { bg: '#fdf2f8', color: '#db2777' },
  { bg: '#f0fdf4', color: '#16a34a' },
  { bg: '#faf5ff', color: '#9333ea' },
  { bg: '#fff7ed', color: '#ea580c' },
  { bg: '#f0f9ff', color: '#0284c7' },
];

const getAvatarBg = (str) => {
  let hash = 0;
  for (let i = 0; i < (str || '').length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

const buildDiceBearAvatarUrl = (account) => {
  if (account?.avatar_url) return account.avatar_url;
  const seed = encodeURIComponent(account?.avatar_seed || account?.email || account?.username || `user-${account?.id || 'sv'}`);
  const style = encodeURIComponent(account?.avatar_style || 'pixel-art');
  return `https://api.dicebear.com/10.x/${style}/svg?seed=${seed}`;
};

const AccountManagement = () => {
  const { currentUser, logout } = useAuth();
  const currentRole = (currentUser?.role || '').toLowerCase();
  const isAdmin = ['admin', 'administrator'].includes(currentRole);
  const isCenterUser = currentRole === 'center';
  const [summary, setSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [centers, setCenters] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const currentCenterId = isCenterUser && currentUser?.id ? String(currentUser.id) : '';

  // Account creation state
  const [accountType, setAccountType] = useState('student'); // 'student' | 'center' | 'admin'
  const [studentMethod, setStudentMethod] = useState('quick'); // 'quick' | 'manual' | 'bulk'

  // Quick form
  const [quickForm, setQuickForm] = useState({
    quantity: 10,
    prefix: 'sv',
    plan_type: 'standard',
    expires_date: getTodayPlusMonths(1),
    exam_quota: 10,
    is_unlimited_quota: false,
    center_id: '',
    classroom_id: '',
  });

  // Manual student form
  const [manualForm, setManualForm] = useState({
    full_name: '',
    email: '',
    password: '',
    plan_type: 'standard',
    expires_date: getTodayPlusMonths(1),
    exam_quota: 10,
    is_unlimited_quota: false,
    center_id: '',
    classroom_id: '',
  });

  // Bulk form
  const [bulkText, setBulkText] = useState('');
  const [bulkExpiresDate, setBulkExpiresDate] = useState(getTodayPlusMonths(1));
  const [bulkExamQuota, setBulkExamQuota] = useState(10);
  const [bulkUnlimitedQuota, setBulkUnlimitedQuota] = useState(false);
  const [bulkCenterId, setBulkCenterId] = useState('');
  const [bulkClassroomId, setBulkClassroomId] = useState('');

  // Center form
  const [centerForm, setCenterForm] = useState({
    full_name: '',
    email: '',
    password: '',
    expires_date: getTodayPlusMonths(12),
  });

  // Admin form
  const [adminForm, setAdminForm] = useState({
    full_name: '',
    email: '',
    password: '',
    expires_date: '',
  });

  // Modals state
  const [quotaModalAccount, setQuotaModalAccount] = useState(null);
  const [quotaAddValue, setQuotaAddValue] = useState(5);
  const [quotaSetValue, setQuotaSetValue] = useState('');
  const [extendModalAccount, setExtendModalAccount] = useState(null);
  const [extendDateValue, setExtendDateValue] = useState('');
  const [passwordModalAccount, setPasswordModalAccount] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [generatedResults, setGeneratedResults] = useState(null);
  const [copied, setCopied] = useState(false);

  // Export TXT / Copy Modal
  const [exportModalAccounts, setExportModalAccounts] = useState(null);
  const [exportFormatTab, setExportFormatTab] = useState('detail'); // 'detail' | 'compact' | 'tsv'
  const [exportCopied, setExportCopied] = useState(false);

  // Quick Classroom Creation Modal
  const [showCreateClassModal, setShowCreateClassModal] = useState(false);
  const [newClassForm, setNewClassForm] = useState({
    code: '',
    name: '',
    description: '',
    starts_at: '',
    ends_at: '',
  });
  const [creatingClass, setCreatingClass] = useState(false);
  const [newClassDurationChoice, setNewClassDurationChoice] = useState(1);

  // Filter & search
  const [filterTab, setFilterTab] = useState('all'); // 'all' | 'active' | 'activated' | 'quota_exceeded' | 'pending' | 'expired' | 'expiring' | 'locked'
  const [roleFilter, setRoleFilter] = useState('all'); // 'all' | 'student' | 'center' | 'admin'
  const [centerFilter, setCenterFilter] = useState('all'); // 'all' | 'none' | centerId
  const [classroomFilter, setClassroomFilter] = useState('all'); // 'all' | 'none' | classroomId
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Selection & Pagination
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  // Bulk Delete Modal state
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [accountsToDelete, setAccountsToDelete] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Account Detail Drawer state
  const [detailDrawerAccount, setDetailDrawerAccount] = useState(null);

  // Edit Account Modal state
  const [editModalAccount, setEditModalAccount] = useState(null);
  const [editForm, setEditForm] = useState({
    full_name: '',
    phone: '',
    personal_email: '',
    exam_quota: 10,
    is_unlimited_quota: false,
    expires_date: '',
    account_status: 'active',
    new_password: '',
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // Activity Audit Log state
  const [showAuditLogModal, setShowAuditLogModal] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);

  const selectedQuickClassroom = useMemo(
    () => classrooms.find((c) => String(c.id) === String(quickForm.classroom_id)),
    [classrooms, quickForm.classroom_id],
  );

  const selectedManualClassroom = useMemo(
    () => classrooms.find((c) => String(c.id) === String(manualForm.classroom_id)),
    [classrooms, manualForm.classroom_id],
  );

  const selectedBulkClassroom = useMemo(
    () => classrooms.find((c) => String(c.id) === String(bulkClassroomId)),
    [classrooms, bulkClassroomId],
  );

  const importantNotifications = useMemo(
    () => buildAccountNotifications({ accounts, classrooms, currentUser }),
    [accounts, classrooms, currentUser],
  );

  const currentCenterAccount = useMemo(
    () => centers.find((c) => String(c.id) === currentCenterId) || currentUser,
    [centers, currentCenterId, currentUser],
  );

  const currentCenterLabel = currentCenterAccount?.full_name || currentCenterAccount?.email || 'Trung tâm hiện tại';

  const quickClassroomOptions = useMemo(() => {
    const centerId = isCenterUser ? currentCenterId : quickForm.center_id;
    return classrooms.filter((cls) => classroomMatchesCenter(cls, centerId));
  }, [classrooms, currentCenterId, isCenterUser, quickForm.center_id]);

  const manualClassroomOptions = useMemo(() => {
    const centerId = isCenterUser ? currentCenterId : manualForm.center_id;
    return classrooms.filter((cls) => classroomMatchesCenter(cls, centerId));
  }, [classrooms, currentCenterId, isCenterUser, manualForm.center_id]);

  const bulkClassroomOptions = useMemo(() => {
    const centerId = isCenterUser ? currentCenterId : bulkCenterId;
    return classrooms.filter((cls) => classroomMatchesCenter(cls, centerId));
  }, [bulkCenterId, classrooms, currentCenterId, isCenterUser]);

  const renderCenterScopeNotice = () => {
    if (!isCenterUser) return null;
    return (
      <div className="center-scope-card">
        <div className="center-scope-icon">
          <BuildingOffice2Icon />
        </div>
        <div>
          <span>Trung tâm đang đăng nhập</span>
          <strong>{currentCenterLabel}</strong>
          <p>Học viên và lớp học tạo mới sẽ tự thuộc trung tâm này. Hệ thống không cho chọn hoặc thấy trung tâm khác.</p>
        </div>
      </div>
    );
  };

  const handleQuickClassroomChange = (classroomId) => {
    const selectedCls = classrooms.find((c) => String(c.id) === String(classroomId));
    setQuickForm((prev) => {
      const updated = { ...prev, classroom_id: classroomId };
      if (selectedCls?.ends_at) {
        updated.expires_date = selectedCls.ends_at.split('T')[0];
      }
      return updated;
    });
  };

  const handleManualClassroomChange = (classroomId) => {
    const selectedCls = classrooms.find((c) => String(c.id) === String(classroomId));
    setManualForm((prev) => {
      const updated = { ...prev, classroom_id: classroomId };
      if (selectedCls?.ends_at) {
        updated.expires_date = selectedCls.ends_at.split('T')[0];
      }
      return updated;
    });
  };

  const handleBulkClassroomChange = (classroomId) => {
    const selectedCls = classrooms.find((c) => String(c.id) === String(classroomId));
    setBulkClassroomId(classroomId);
    if (selectedCls?.ends_at) {
      setBulkExpiresDate(selectedCls.ends_at.split('T')[0]);
    }
  };

  const handleOpenCreateClassModal = () => {
    const existingCodes = classrooms.map((c) => (c.code || '').toUpperCase());
    const generatedCode = generateRandomClassCode(currentUser?.full_name || currentUser?.username || '', existingCodes);
    const { starts_at, ends_at } = getInitialClassDates();
    setNewClassForm({
      code: generatedCode,
      name: '',
      description: '',
      starts_at,
      ends_at,
    });
    setNewClassDurationChoice(1);
    setShowCreateClassModal(true);
  };

  const handleRandomizeNewClassCode = () => {
    const existingCodes = classrooms.map((c) => (c.code || '').toUpperCase());
    const generatedCode = generateRandomClassCode(currentUser?.full_name || currentUser?.username || '', existingCodes);
    setNewClassForm((prev) => ({ ...prev, code: generatedCode }));
    flashNotice(`Đã tạo mã lớp ngẫu nhiên mới: ${generatedCode}`);
  };

  const handleApplyNewClassDuration = (months) => {
    setNewClassDurationChoice(months);
    const newEnd = calculateEndDate(newClassForm.starts_at, months);
    setNewClassForm((prev) => ({ ...prev, ends_at: newEnd }));
  };

  const handleCreateNewClassroom = async (e) => {
    e.preventDefault();
    setCreatingClass(true);
    setError('');
    try {
      const created = await createClassroom({
        code: newClassForm.code.trim(),
        name: newClassForm.name.trim(),
        description: newClassForm.description?.trim() || null,
        starts_at: newClassForm.starts_at ? `${newClassForm.starts_at}:00` : null,
        ends_at: newClassForm.ends_at ? `${newClassForm.ends_at}:00` : null,
      });
      flashNotice(`Đã tạo lớp học mới: ${created.code} - ${created.name}`);
      const updatedClassrooms = await getClassrooms();
      setClassrooms(updatedClassrooms);

      if (accountType === 'student') {
        if (studentMethod === 'quick') {
          setQuickForm((prev) => ({
            ...prev,
            classroom_id: created.id,
            expires_date: created.ends_at ? created.ends_at.split('T')[0] : prev.expires_date,
          }));
        } else if (studentMethod === 'manual') {
          setManualForm((prev) => ({
            ...prev,
            classroom_id: created.id,
            expires_date: created.ends_at ? created.ends_at.split('T')[0] : prev.expires_date,
          }));
        } else if (studentMethod === 'bulk') {
          setBulkClassroomId(created.id);
          if (created.ends_at) {
            setBulkExpiresDate(created.ends_at.split('T')[0]);
          }
        }
      }
      setShowCreateClassModal(false);
      setNewClassForm({ code: '', name: '', description: '', starts_at: '', ends_at: '' });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Lỗi tạo lớp học mới.');
    } finally {
      setCreatingClass(false);
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [summaryData, accountData, centerData, classroomData] = await Promise.all([
        userService.getAccountSummary(),
        userService.getTemporaryAccounts(),
        userService.getCenters(),
        getClassrooms().catch(() => []),
      ]);
      setSummary(summaryData);
      setAccounts(accountData);
      setCenters(centerData);
      setClassrooms(classroomData || []);
      if (isCenterUser && currentCenterId) {
        setQuickForm((prev) => ({ ...prev, center_id: currentCenterId }));
        setManualForm((prev) => ({ ...prev, center_id: currentCenterId }));
        setBulkCenterId(currentCenterId);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Không tải được danh sách tài khoản.');
    } finally {
      setLoading(false);
    }
  }, [currentCenterId, isCenterUser]);

  useEffect(() => {
    loadData();

  }, [loadData]);

  const expiringSoon = useMemo(
    () =>
      accounts.filter((account) => {
        const left = daysLeft(account.expires_at);
        return getStatus(account) === 'active' && left !== null && left <= 7 && left >= 0;
      }),
    [accounts],
  );

  const quotaExceededAccounts = useMemo(
    () => accounts.filter((account) => account.role === 'student' && getStatus(account) === 'quota_exceeded'),
    [accounts],
  );

  const pendingAccounts = useMemo(
    () => accounts.filter((account) => account.role === 'student' && !account.first_login_completed),
    [accounts],
  );

  const activatedAccounts = useMemo(
    () => accounts.filter((account) => account.role === 'student' && account.first_login_completed),
    [accounts],
  );

  const expiredAccounts = useMemo(
    () => accounts.filter((account) => getStatus(account) === 'expired'),
    [accounts],
  );

  const lockedAccounts = useMemo(
    () => accounts.filter((account) => getStatus(account) === 'locked'),
    [accounts],
  );

  const adminAccounts = useMemo(
    () => accounts.filter((account) => isAdminRole(account.role)),
    [accounts],
  );

  const filterClassroomOptions = useMemo(() => {
    if (isCenterUser) {
      return classrooms.filter((cls) => classroomMatchesCenter(cls, currentCenterId));
    }
    if (centerFilter !== 'all' && centerFilter !== 'none') {
      return classrooms.filter((cls) => classroomMatchesCenter(cls, centerFilter));
    }
    return classrooms;
  }, [classrooms, centerFilter, currentCenterId, isCenterUser]);

  const filteredAccounts = useMemo(() => {
    let list = accounts;

    if (roleFilter !== 'all') {
      if (roleFilter === 'admin') {
        list = list.filter((a) => isAdminRole(a.role));
      } else {
        list = list.filter((a) => a.role === roleFilter);
      }
    }

    if (centerFilter !== 'all') {
      if (centerFilter === 'none') {
        list = list.filter((acc) => acc.role === 'student' && !acc.center_id);
      } else {
        list = list.filter(
          (acc) =>
            String(acc.center_id) === String(centerFilter) ||
            (acc.role === 'center' && String(acc.id) === String(centerFilter)),
        );
      }
    }

    if (classroomFilter !== 'all') {
      if (classroomFilter === 'none') {
        list = list.filter((acc) => acc.role === 'student' && !acc.classroom_id);
      } else {
        list = list.filter((acc) => String(acc.classroom_id) === String(classroomFilter));
      }
    }

    if (filterTab === 'active') {
      list = list.filter((account) => getStatus(account) === 'active');
    } else if (filterTab === 'activated') {
      list = list.filter((account) => account.role === 'student' && account.first_login_completed);
    } else if (filterTab === 'quota_exceeded') {
      list = list.filter((account) => account.role === 'student' && getStatus(account) === 'quota_exceeded');
    } else if (filterTab === 'pending') {
      list = list.filter((account) => account.role === 'student' && !account.first_login_completed);
    } else if (filterTab === 'expired') {
      list = list.filter((account) => getStatus(account) === 'expired');
    } else if (filterTab === 'expiring') {
      list = expiringSoon;
    } else if (filterTab === 'locked') {
      list = lockedAccounts;
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (acc) =>
          (acc.username && acc.username.toLowerCase().includes(q)) ||
          (acc.full_name && acc.full_name.toLowerCase().includes(q)) ||
          (acc.email && acc.email.toLowerCase().includes(q)) ||
          (acc.phone && acc.phone.toLowerCase().includes(q)) ||
          (acc.phone_number && acc.phone_number.toLowerCase().includes(q)) ||
          (acc.classroom_name && acc.classroom_name.toLowerCase().includes(q)) ||
          (acc.classroom_code && acc.classroom_code.toLowerCase().includes(q)) ||
          (acc.center_name && acc.center_name.toLowerCase().includes(q)),
      );
    }

    return list;
  }, [accounts, filterTab, roleFilter, centerFilter, classroomFilter, expiringSoon, lockedAccounts, searchQuery]);

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterTab, roleFilter, centerFilter, classroomFilter, searchQuery]);

  const totalPages = Math.ceil(filteredAccounts.length / pageSize) || 1;

  const paginatedAccounts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAccounts.slice(start, start + pageSize);
  }, [filteredAccounts, currentPage, pageSize]);

  const deletableTargetAccounts = useMemo(() => {
    const baseList =
      selectedIds.length > 0
        ? accounts.filter((a) => selectedIds.includes(a.id))
        : filteredAccounts;
    return baseList.filter((a) => a.role === 'student' && a.id !== currentUser?.id);
  }, [accounts, filteredAccounts, selectedIds, currentUser]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(paginatedAccounts.map((a) => a.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const flashNotice = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(''), 3500);
  };

  // Quick generate
  const handleQuickGenerate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const effectiveCenterId = isCenterUser ? currentCenterId : quickForm.center_id;
      const payload = {
        quantity: Number(quickForm.quantity),
        prefix: quickForm.prefix.trim() || 'sv',
        plan_type: 'standard',
        expires_date: quickForm.expires_date,
        expires_at: quickForm.expires_date ? `${quickForm.expires_date}T23:59:59` : undefined,
        exam_quota: quickForm.is_unlimited_quota ? 0 : Number(quickForm.exam_quota || 0),
        center_id: effectiveCenterId ? Number(effectiveCenterId) : undefined,
        classroom_id: quickForm.classroom_id || undefined,
      };
      const res = await userService.generateAccounts(payload);
      setGeneratedResults(res);
      flashNotice(`Đã cấp thành công ${res.count} tài khoản ngẫu nhiên.`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Lỗi cấp tài khoản ngẫu nhiên.');
    } finally {
      setSaving(false);
    }
  };

  // Manual create student
  const handleCreateManualStudent = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const effectiveCenterId = isCenterUser ? currentCenterId : manualForm.center_id;
      const payload = {
        email: manualForm.email.trim(),
        full_name: manualForm.full_name.trim(),
        password: manualForm.password,
        role: 'student',
        plan_type: 'standard',
        expires_date: manualForm.expires_date,
        expires_at: manualForm.expires_date ? `${manualForm.expires_date}T23:59:59` : undefined,
        exam_quota: manualForm.is_unlimited_quota ? 0 : Number(manualForm.exam_quota || 0),
        center_id: effectiveCenterId ? Number(effectiveCenterId) : undefined,
        classroom_id: manualForm.classroom_id || undefined,
      };
      await userService.createAccount(payload);
      flashNotice(`Đã tạo tài khoản học viên ${manualForm.full_name}.`);
      setManualForm({
        full_name: '',
        email: '',
        password: '',
        plan_type: 'standard',
        expires_date: getTodayPlusMonths(1),
        exam_quota: 10,
        is_unlimited_quota: false,
        center_id: isCenterUser ? currentCenterId : '',
        classroom_id: '',
      });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Lỗi tạo tài khoản.');
    } finally {
      setSaving(false);
    }
  };

  // Parse live preview for bulk paste
  const parsedBulkPreview = useMemo(() => {
    if (!bulkText.trim()) return [];
    const lines = bulkText.split(/\r?\n/).filter((l) => l.trim().length > 0);
    const existingEmails = new Set(accounts.map((a) => (a.email || '').toLowerCase()));
    const existingUsernames = new Set(accounts.map((a) => (a.username || '').toLowerCase()));
    const seenInBatch = new Set();

    return lines.map((line, idx) => {
      let parts = [];
      if (line.includes('\t')) parts = line.split('\t');
      else if (line.includes('|')) parts = line.split('|');
      else if (line.includes(',')) parts = line.split(',');
      else parts = line.trim().split(/\s+/);

      parts = parts.map((p) => p.trim());
      let name = '';
      let email = '';
      let password = '';

      if (parts.length >= 3) {
        name = parts[0];
        email = parts[1];
        password = parts[2];
      } else if (parts.length === 2) {
        name = parts[0];
        email = parts[1];
        password = 'SV' + Math.floor(100000 + Math.random() * 900000);
      } else if (parts.length === 1) {
        email = parts[0];
        name = parts[0];
        password = 'SV' + Math.floor(100000 + Math.random() * 900000);
      }

      const cleanEmail = (email || '').toLowerCase();
      let status = 'valid';
      let errorMsg = '';

      if (!cleanEmail || cleanEmail.length < 3) {
        status = 'error';
        errorMsg = 'Tên đăng nhập không hợp lệ';
      } else if (seenInBatch.has(cleanEmail)) {
        status = 'duplicate';
        errorMsg = 'Trùng trong danh sách dán';
      } else if (existingEmails.has(cleanEmail) || existingUsernames.has(cleanEmail)) {
        status = 'exists';
        errorMsg = 'Đã có trên hệ thống';
      } else {
        seenInBatch.add(cleanEmail);
      }

      return {
        index: idx + 1,
        name: name || 'Học viên',
        email: cleanEmail,
        password: password || '123456',
        status,
        errorMsg,
      };
    });
  }, [bulkText, accounts]);

  // Bulk create
  const handleBulkCreate = async () => {
    const validItems = parsedBulkPreview.filter((item) => item.status === 'valid');
    if (!validItems.length) {
      alert('Không có dòng hợp lệ nào để tạo tài khoản.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const effectiveCenterId = isCenterUser ? currentCenterId : bulkCenterId;
      const accountsList = validItems.map((item) => ({
        full_name: item.name,
        email: item.email,
        password: item.password,
        role: 'student',
        plan_type: 'standard',
        expires_date: bulkExpiresDate,
        expires_at: bulkExpiresDate ? `${bulkExpiresDate}T23:59:59` : undefined,
        exam_quota: bulkUnlimitedQuota ? 0 : Number(bulkExamQuota || 0),
        center_id: effectiveCenterId ? Number(effectiveCenterId) : undefined,
        classroom_id: bulkClassroomId || undefined,
      }));
      const res = await userService.bulkCreateAccounts(accountsList);
      flashNotice(`✓ Đã tạo thành công ${res.created_count || accountsList.length} tài khoản học viên.`);
      setBulkText('');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Lỗi tạo danh sách học viên.');
    } finally {
      setSaving(false);
    }
  };

  // Safe Bulk Floating Actions Handlers
  const handleBulkExtendMonths = async (months) => {
    if (!selectedIds.length) return;
    setSaving(true);
    setError('');
    try {
      const today = new Date();
      today.setMonth(today.getMonth() + months);
      const dateStr = today.toISOString().split('T')[0];
      await Promise.all(
        selectedIds.map((id) => userService.extendAccount(id, { expires_at: `${dateStr}T23:59:59` }))
      );
      flashNotice(`✓ Đã gia hạn +${months} tháng cho ${selectedIds.length} tài khoản!`);
      await loadData();
      setSelectedIds([]);
    } catch (err) {
      setError('Lỗi khi gia hạn hàng loạt: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleBulkAddQuota = async (addAmount) => {
    if (!selectedIds.length) return;
    setSaving(true);
    setError('');
    try {
      await Promise.all(
        selectedIds.map((id) => userService.addExamQuota(id, { add_quota: addAmount }))
      );
      flashNotice(`✓ Đã cộng thêm +${addAmount} lượt thi cho ${selectedIds.length} tài khoản!`);
      await loadData();
      setSelectedIds([]);
    } catch (err) {
      setError('Lỗi khi cộng lượt thi: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleBulkToggleLock = async () => {
    if (!selectedIds.length) return;
    setSaving(true);
    setError('');
    try {
      const firstAcc = accounts.find((a) => a.id === selectedIds[0]);
      const shouldLock = firstAcc?.account_status !== 'locked';
      await Promise.all(
        selectedIds.map((id) =>
          shouldLock ? userService.lockAccount(id) : userService.unlockAccount(id)
        )
      );
      flashNotice(`✓ Đã ${shouldLock ? 'khóa' : 'mở khóa'} ${selectedIds.length} tài khoản!`);
      await loadData();
      setSelectedIds([]);
    } catch (err) {
      setError('Lỗi khi đổi trạng thái: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Edit Account Handlers
  const handleOpenEditModal = (account) => {
    setEditModalAccount(account);
    setEditForm({
      full_name: account.full_name || '',
      phone: account.phone || account.phone_number || '',
      personal_email: account.personal_email || '',
      exam_quota: account.exam_quota != null ? account.exam_quota : 10,
      is_unlimited_quota: account.exam_quota === 0 || account.exam_quota == null,
      expires_date: account.expires_at ? account.expires_at.split('T')[0] : getTodayPlusMonths(1),
      account_status: account.account_status || 'active',
      new_password: '',
    });
  };

  const handleSaveEditAccount = async (e) => {
    e.preventDefault();
    if (!editModalAccount) return;
    setSavingEdit(true);
    setError('');
    try {
      const payload = {
        full_name: editForm.full_name.trim(),
        phone: editForm.phone.trim() || null,
        personal_email: editForm.personal_email.trim() || null,
        exam_quota: editForm.is_unlimited_quota ? 0 : Number(editForm.exam_quota || 0),
        expires_at: editForm.expires_date ? `${editForm.expires_date}T23:59:59` : null,
        account_status: editForm.account_status,
      };
      if (editForm.new_password && editForm.new_password.trim()) {
        payload.password = editForm.new_password.trim();
      }
      await userService.updateUser(editModalAccount.id, payload);
      flashNotice(`✓ Đã cập nhật thành công tài khoản: ${editModalAccount.email}`);
      setEditModalAccount(null);
      if (detailDrawerAccount && detailDrawerAccount.id === editModalAccount.id) {
        setDetailDrawerAccount(null);
      }
      await loadData();
    } catch (err) {
      setError('Lỗi khi lưu tài khoản: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingEdit(false);
    }
  };

  // Activity Audit Log Handler
  const handleOpenAuditLog = async () => {
    setShowAuditLogModal(true);
    setLoadingAuditLogs(true);
    try {
      const records = await userService.getAccountAuditLog(50);
      setAuditLogs(records);
    } catch (err) {
      console.error('Lỗi khi tải nhật ký hoạt động:', err);
    } finally {
      setLoadingAuditLogs(false);
    }
  };


  // Center create
  const handleCreateCenter = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        email: centerForm.email.trim(),
        full_name: centerForm.full_name.trim(),
        password: centerForm.password,
        role: 'center',
        plan_type: 'standard',
        expires_date: centerForm.expires_date,
        expires_at: centerForm.expires_date ? `${centerForm.expires_date}T23:59:59` : undefined,
      };
      await userService.createAccount(payload);
      flashNotice(`Đã tạo tài khoản Trung tâm: ${centerForm.full_name}.`);
      setCenterForm({
        full_name: '',
        email: '',
        password: '',
        expires_date: getTodayPlusMonths(12),
      });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Lỗi tạo tài khoản Trung tâm.');
    } finally {
      setSaving(false);
    }
  };

  // Admin create
  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        email: adminForm.email.trim(),
        full_name: adminForm.full_name.trim(),
        password: adminForm.password,
        role: 'admin',
        plan_type: 'standard',
        expires_date: adminForm.expires_date || undefined,
        expires_at: adminForm.expires_date ? `${adminForm.expires_date}T23:59:59` : undefined,
      };
      await userService.createAccount(payload);
      flashNotice(`Đã tạo tài khoản Quản trị viên: ${adminForm.full_name}.`);
      setAdminForm({
        full_name: '',
        email: '',
        password: '',
        expires_date: '',
      });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Lỗi tạo tài khoản Quản trị viên.');
    } finally {
      setSaving(false);
    }
  };

  // Quota action
  const handleSaveQuota = async (e) => {
    e.preventDefault();
    if (!quotaModalAccount) return;
    setSaving(true);
    try {
      if (quotaSetValue !== '') {
        await userService.updateExamQuota(quotaModalAccount.id, {
          exam_quota: Number(quotaSetValue),
          reason: 'Đặt lại tổng hạn mức lượt thi',
        });
      } else {
        await userService.addExamQuota(quotaModalAccount.id, {
          add_quota: Number(quotaAddValue),
          reason: 'Cộng thêm lượt thi nhanh',
        });
      }
      flashNotice(`Đã cập nhật lượt thi cho ${quotaModalAccount.full_name || quotaModalAccount.email}.`);
      setQuotaModalAccount(null);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Lỗi cập nhật lượt thi.');
    } finally {
      setSaving(false);
    }
  };

  // Extend action
  const handleSaveExtend = async (e) => {
    e.preventDefault();
    if (!extendModalAccount || !extendDateValue) return;
    setSaving(true);
    try {
      if (extendDateValue === 'unlimited') {
        await userService.extendExpiry(extendModalAccount.id, {
          unlimited_expiry: true,
        });
      } else {
        await userService.extendExpiry(extendModalAccount.id, {
          expires_at: `${extendDateValue}T23:59:59`,
        });
      }
      flashNotice(`Đã gia hạn ngày cho ${extendModalAccount.full_name || extendModalAccount.email}.`);
      setExtendModalAccount(null);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Lỗi gia hạn ngày.');
    } finally {
      setSaving(false);
    }
  };

  // Reset password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!passwordModalAccount || !newPassword) return;
    setSaving(true);
    try {
      await userService.resetPassword(passwordModalAccount.id, newPassword);
      flashNotice(`Đã đổi mật khẩu cho ${passwordModalAccount.full_name || passwordModalAccount.email}.`);
      setPasswordModalAccount(null);
      setNewPassword('');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Lỗi đổi mật khẩu.');
    } finally {
      setSaving(false);
    }
  };

  // Lock toggle
  const handleLockToggle = async (account) => {
    const isLocked = account.account_status === 'locked';
    const action = isLocked ? 'unlock' : 'lock';
    try {
      await userService.updateAccountStatus(account.id, action);
      flashNotice(`Đã ${isLocked ? 'mở khóa' : 'khóa'} tài khoản ${account.full_name || account.email}.`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Lỗi cập nhật trạng thái.');
    }
  };

  const handleRandomizeAvatar = async (account) => {
    setSaving(true);
    setError('');
    try {
      await userService.randomizeAvatar(account.id);
      flashNotice(`Đã đổi avatar cho ${account.full_name || account.email}.`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Lỗi đổi avatar.');
    } finally {
      setSaving(false);
    }
  };

  // Delete single account
  const handleDelete = async (account) => {
    if (account.id === currentUser?.id) {
      setError('Không thể tự xóa tài khoản đang đăng nhập.');
      return;
    }
    if (account.id === 1 || (account.email || '').toLowerCase() === 'admin') {
      setError('Không thể xóa tài khoản Quản trị gốc (admin).');
      return;
    }
    const isCenter = account.role === 'center';
    const isTargetAdmin = isAdminRole(account.role);
    const msg = isTargetAdmin
      ? `⚠️ CẢNH BÁO NGUY HIỂM: Bạn đang chuẩn bị xóa TÀI KHOẢN QUẢN TRỊ VIÊN "${account.full_name || account.email}"!\n\nBạn có THỰC SỰ chắc chắn muốn xóa vĩnh viễn không?`
      : isCenter
        ? `⚠️ CẢNH BÁO NGUY HIỂM: Bạn đang chuẩn bị xóa TRUNG TÂM ĐỐI TÁC "${account.full_name || account.email}"!\nXóa trung tâm sẽ ảnh hưởng tới dữ liệu các lớp học trực thuộc.\n\nBạn có THỰC SỰ chắc chắn muốn xóa vĩnh viễn không?`
        : `Bạn có chắc chắn muốn xóa vĩnh viễn tài khoản "${account.full_name || account.email}" không? Thao tác này không thể hoàn tác.`;

    if (!window.confirm(msg)) {
      return;
    }
    try {
      await userService.deleteAccount(account.id);
      flashNotice(`Đã xóa tài khoản ${account.full_name || account.email}.`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Lỗi xóa tài khoản.');
    }
  };

  // Bulk Delete (Selected accounts OR Filtered student accounts)
  const handleOpenBulkDeleteModal = () => {
    const hasSelection = selectedIds && selectedIds.length > 0;
    const baseList = hasSelection
      ? accounts.filter((a) => selectedIds.includes(a.id) && a.id !== currentUser?.id)
      : filteredAccounts.filter((a) => a.id !== currentUser?.id);

    // Prevent accidental bulk deletion of partner centers or admins when manually selected
    if (hasSelection) {
      const protectedInList = baseList.filter((a) => a.role !== 'student');
      if (protectedInList.length > 0) {
        const confirmRemoveProtected = window.confirm(
          `⚠️ CẢNH BÁO: Trong danh sách bạn chọn có ${protectedInList.length} tài khoản Quản trị / Trung tâm đối tác.\n` +
          `Để đảm bảo an toàn, hệ thống sẽ BỎ QUA tài khoản Quản trị & Trung tâm và chỉ xóa các tài khoản Học viên.\n\n` +
          `Bấm OK để tiếp tục xóa các tài khoản học viên còn lại.`
        );
        if (!confirmRemoveProtected) return;
      }
    }

    const safeList = baseList.filter((a) => a.role === 'student');
    if (safeList.length === 0) {
      flashNotice('⚠️ Không có tài khoản học viên nào phù hợp để xóa (Hệ thống tự động bảo vệ tài khoản Quản trị & Trung tâm).');
      return;
    }

    setAccountsToDelete(safeList);
    setDeleteConfirmText('');
    setShowBulkDeleteModal(true);
  };

  const isBulkDeleteConfirmed = ['OK', 'XÓA', 'XOÁ', 'XOA'].includes(
    deleteConfirmText.trim().toUpperCase()
  );

  const handleConfirmBulkDelete = async () => {
    if (!accountsToDelete || accountsToDelete.length === 0) return;
    if (!isBulkDeleteConfirmed) {
      setError('Vui lòng gõ chữ "OK" vào ô xác nhận để mở khóa thao tác.');
      return;
    }

    setBulkDeleting(true);
    setError('');
    try {
      const ids = accountsToDelete.map((a) => a.id);
      const res = await userService.bulkDeleteAccounts(ids);
      flashNotice(`✓ Đã xóa thành công ${res.deleted_count || ids.length} tài khoản khỏi hệ thống!`);
      setSelectedIds([]);
      setShowBulkDeleteModal(false);
      setAccountsToDelete([]);
      setDeleteConfirmText('');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Lỗi khi xóa hàng loạt tài khoản.');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Copy generated
  const handleCopyAccounts = () => {
    if (!generatedResults?.accounts) return;
    const text = generatedResults.accounts
      .map((a) => `${a.username} | Pass: ${a.password} | Lượt: ${a.exam_quota || 'Vô hạn'} | Hạn: ${formatDate(a.expires_at)}`)
      .join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };


  // Memoized formatted text for export modal (TXT, Compact, TSV)
  const exportText = useMemo(() => {
    if (!exportModalAccounts || exportModalAccounts.length === 0) return '';
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const nowStr = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;

    if (exportFormatTab === 'compact') {
      return exportModalAccounts
        .map((a, i) => {
          const pass = a.password || a.initial_password || '(Đã bảo mật)';
          const cls = a.classroom_name || (classrooms.find((c) => String(c.id) === String(a.classroom_id))?.name) || '—';
          const quota = a.exam_quota && a.exam_quota > 0 ? `${a.exam_quota} lượt` : 'Không giới hạn';
          const exp = formatDate(a.expires_at) || 'Không giới hạn';
          return `${i + 1}. ${a.username || a.email} | Pass: ${pass} | ${a.full_name || ''} | Lớp: ${cls} | Hạn: ${exp} | Lượt: ${quota}`;
        })
        .join('\n');
    }

    if (exportFormatTab === 'tsv') {
      const headers = ['STT', 'Họ và tên', 'Tài khoản / Email', 'Mật khẩu', 'Lớp học', 'Hạn mức thi', 'Đã thi', 'Đã luyện tập', 'Hạn sử dụng', 'Trạng thái'];
      const rows = exportModalAccounts.map((a, i) => {
        const pass = a.password || a.initial_password || '(Đã bảo mật)';
        const cls = a.classroom_name || (classrooms.find((c) => String(c.id) === String(a.classroom_id))?.name) || '—';
        const quota = a.exam_quota && a.exam_quota > 0 ? `${a.exam_quota} lượt` : 'Không giới hạn';
        const used = `${a.exam_used || 0} lượt`;
        const practice = `${a.practice_used || 0} lượt`;
        const exp = formatDate(a.expires_at) || 'Không giới hạn';
        const status = (a.effective_account_status || a.account_status) === 'active' ? 'Đang hoạt động' : 'Đã khóa';
        return [i + 1, a.full_name || a.email, a.username || a.email, pass, cls, quota, used, practice, exp, status].join('\t');
      });
      return [headers.join('\t'), ...rows].join('\n');
    }

    // Default: 'detail' (Đẹp mắt, dễ đọc, gửi qua Zalo / Nhóm lớp)
    const lines = [
      '================================================================================',
      '      TRUNG TÂM ĐÀO TẠO SAO VIỆT - DANH SÁCH TÀI KHOẢN VÀ MẬT KHẨU',
      '================================================================================',
      `Thời gian xuất: ${nowStr}`,
      `Tổng số: ${exportModalAccounts.length} tài khoản`,
      '--------------------------------------------------------------------------------',
      '',
    ];

    exportModalAccounts.forEach((a, i) => {
      const pass = a.password || a.initial_password || '(Đã bảo mật)';
      const cls = a.classroom_name || (classrooms.find((c) => String(c.id) === String(a.classroom_id))?.name) || '—';
      const quota = a.exam_quota && a.exam_quota > 0 ? `${a.exam_quota} lượt` : 'Không giới hạn';
      const exp = formatDate(a.expires_at) || 'Không giới hạn';
      lines.push(`[${i + 1}] ${a.full_name || a.email}`);
      lines.push(`    - Tên đăng nhập: ${a.username || a.email}`);
      lines.push(`    - Mật khẩu: ${pass}`);
      lines.push(`    - Lớp học: ${cls}`);
      lines.push(`    - Hạn mức lượt thi: ${quota}`);
      lines.push(`    - Đã thi: ${a.exam_used || 0} lượt | Đã luyện tập: ${a.practice_used || 0} lượt`);
      lines.push(`    - Hạn dùng: ${exp}`);
      lines.push('');
    });

    lines.push('================================================================================');
    lines.push(`HỌC VIÊN ĐĂNG NHẬP TẠI: ${window.location.origin}/login`);
    lines.push('================================================================================');

    return lines.join('\n');
  }, [exportModalAccounts, exportFormatTab, classrooms]);

  // Copy export text to clipboard
  const handleCopyExportText = () => {
    if (!exportText) return;
    navigator.clipboard.writeText(exportText);
    setExportCopied(true);
    flashNotice('✓ Đã sao chép toàn bộ danh sách! Bạn có thể dán vào Zalo / Notepad / Word / Excel.');
    setTimeout(() => setExportCopied(false), 2500);
  };

  // Download export as .TXT file
  const handleDownloadTxtFile = () => {
    if (!exportText) return;
    const blob = new Blob([exportText], { type: 'text/plain;charset=utf-8' });
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const dateStamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
    const fileName = `Danh_sach_tai_khoan_Sao_Viet_${dateStamp}.txt`;

    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, fileName);
    } else {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        try {
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } catch (e) {}
      }, 30000);
    }
    flashNotice(`✓ Đã tải file: ${fileName}`);
  };

  // Open Export Modal for filtered accounts
  const handleExportFilteredAccounts = () => {
    const listToExport = selectedIds.length > 0
      ? filteredAccounts.filter((a) => selectedIds.includes(a.id))
      : filteredAccounts;

    if (!listToExport || listToExport.length === 0) {
      alert('Không có tài khoản nào phù hợp với bộ lọc hiện tại để xuất file.');
      return;
    }

    setExportModalAccounts(listToExport);
  };

  // Copy filtered accounts to clipboard for instant Excel paste
  const handleCopyFilteredAccounts = () => {
    const listToExport = selectedIds.length > 0
      ? filteredAccounts.filter((a) => selectedIds.includes(a.id))
      : filteredAccounts;

    if (!listToExport || listToExport.length === 0) return;

    const headers = ['STT', 'Họ và tên', 'Tài khoản / Email', 'Mật khẩu', 'Vai trò', 'Lớp học', 'Trung tâm', 'Lượt thi', 'Hạn dùng', 'Trạng thái'];
    const rows = listToExport.map((account, idx) => {
      const isStudent = account.role === 'student';
      const quotaText = isStudent
        ? account.exam_quota && account.exam_quota > 0
          ? `${account.exam_quota} lượt`
          : 'Không giới hạn'
        : '—';
      const roleText = roleLabel(account.role);
      const classText = account.classroom_name || '—';
      const centerText = account.center_name || '—';
      const passwordText = account.password || account.initial_password || '(Đã bảo mật)';

      return [
        idx + 1,
        account.full_name || account.email,
        account.email,
        passwordText,
        roleText,
        classText,
        centerText,
        quotaText,
        formatDate(account.expires_at),
        statusLabel(account)
      ].join('\t');
    });

    const text = [headers.join('\t'), ...rows].join('\n');
    navigator.clipboard.writeText(text);
    flashNotice(`✓ Đã sao chép ${listToExport.length} tài khoản (kèm mật khẩu) vào bộ nhớ tạm!`);
  };

  // Page numbers for pagination
  const pageNumbers = useMemo(() => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  }, [currentPage, totalPages]);

  const openQuotaModal = (account) => {
    setQuotaModalAccount(account);
    setQuotaAddValue(5);
    setQuotaSetValue('');
  };

  const renderAccountActions = (account, isStudent) => {
    const isProtectedAdmin =
      account.id === currentUser?.id ||
      account.id === 1 ||
      (account.email || '').toLowerCase() === 'admin';

    return (
      <div className="actions-icon-grid-5">
        {isStudent && (
          <button
            className="icon-action-btn btn-plus-quota"
            disabled={saving}
            onClick={() => openQuotaModal(account)}
            type="button"
            data-tooltip="Cấp thêm lượt thi thử"
          >
            <PlusIcon />
            <span>Cấp lượt</span>
          </button>
        )}
        <button
          className="icon-action-btn btn-extend-date"
          disabled={saving}
          onClick={() => {
            setExtendModalAccount(account);
            setExtendDateValue(account.expires_at ? account.expires_at.split('T')[0] : 'unlimited');
          }}
          type="button"
          data-tooltip="Gia hạn ngày sử dụng"
        >
          <CalendarDaysIcon />
        </button>
        <button
          className="icon-action-btn btn-view-detail"
          disabled={saving}
          onClick={() => setDetailDrawerAccount(account)}
          type="button"
          data-tooltip="Xem hồ sơ chi tiết"
        >
          <EyeIcon />
        </button>
        <button
          className="icon-action-btn btn-edit-account"
          disabled={saving}
          onClick={() => handleOpenEditModal(account)}
          type="button"
          data-tooltip="Sửa thông tin & hạn mức"
        >
          <PencilSquareIcon />
        </button>
        <button
          className="icon-action-btn btn-password"
          disabled={saving}
          onClick={() => {
            setPasswordModalAccount(account);
            setNewPassword('');
          }}
          type="button"
          data-tooltip="Đặt lại mật khẩu"
        >
          <KeyIcon />
        </button>
        <button
          className={`icon-action-btn btn-lock ${account.account_status === 'locked' ? 'is-locked' : ''}`}
          disabled={saving || isProtectedAdmin}
          onClick={() => handleLockToggle(account)}
          type="button"
          data-tooltip={
            isProtectedAdmin
              ? 'Tài khoản Quản trị được bảo vệ'
              : account.account_status === 'locked'
                ? 'Mở khóa tài khoản'
                : 'Khóa tài khoản'
          }
          style={isProtectedAdmin ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
        >
          {account.account_status === 'locked' ? <LockOpenIcon /> : <LockClosedIcon />}
        </button>
        <button
          className="icon-action-btn btn-delete"
          disabled={saving || isProtectedAdmin}
          onClick={() => handleDelete(account)}
          type="button"
          data-tooltip={
            isProtectedAdmin
              ? 'Tài khoản Quản trị không thể xóa'
              : 'Xóa vĩnh viễn'
          }
          style={isProtectedAdmin ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
        >
          <TrashIcon />
        </button>
      </div>
    );
  };

  const renderMobileAccountCard = (account) => {
    const isStudent = account.role === 'student';
    const avatarStyle = getAvatarBg(account.full_name || account.email);
    const isSelected = selectedIds.includes(account.id);
    const quotaTotal = Number(account.exam_quota || 0);
    const quotaUnlimited = account.exam_quota === 0 || account.exam_quota == null;
    const quotaPercent = quotaUnlimited
      ? 100
      : Math.min(100, Math.round(((account.exam_used || 0) * 100) / Math.max(1, quotaTotal)));

    return (
      <article className={`mobile-account-card ${isSelected ? 'selected' : ''}`} key={`mobile-${account.id}`}>
        <div className="mobile-account-card-head">
          <label className="mobile-account-select">
            <input checked={isSelected} onChange={() => handleToggleSelect(account.id)} type="checkbox" />
            <span>Chọn</span>
          </label>
          <span className={`account-status-badge ${account.is_classroom_expired ? 'classroom_expired' : getStatus(account)}`}>
            {statusLabel(account)}
          </span>
        </div>

        <div className="mobile-account-profile">
          <div
            className="user-initial-avatar dicebear-avatar-shell"
            style={{ background: avatarStyle.bg, color: avatarStyle.color }}
          >
            <img
              alt=""
              className="dicebear-avatar-img"
              src={buildDiceBearAvatarUrl(account)}
              onError={(event) => {
                event.currentTarget.style.display = 'none';
                event.currentTarget.nextElementSibling.style.display = 'inline';
              }}
            />
            <span className="avatar-fallback-initial">{getInitials(account.full_name, account.email)}</span>
          </div>
          <div className="mobile-account-identity">
            <strong>{account.full_name || roleLabel(account.role)}</strong>
            <span>{account.username || account.email}</span>
            {account.email && <small>{account.email}</small>}
          </div>
        </div>

        {(account.initial_password || account.password || account.classroom_name || account.classroom_code) && (
          <div className="mobile-account-tags">
            {(account.initial_password || account.password) && (
              <span className="mobile-password-chip">Mật khẩu: {account.initial_password || account.password}</span>
            )}
            {(account.classroom_name || account.classroom_code) && (
              <span className={`classroom-student-tag ${account.is_classroom_expired ? 'expired-tag' : ''}`}>
                Lớp: {account.classroom_code ? `${account.classroom_code} - ` : ''}{account.classroom_name || account.classroom_code}
              </span>
            )}
          </div>
        )}

        <div className="mobile-account-facts">
          <div>
            <span>Vai trò</span>
            <strong>{roleLabel(account.role)}</strong>
          </div>
          <div>
            <span>Kích hoạt</span>
            <strong>{!isStudent || account.first_login_completed ? 'Đã kích hoạt' : 'Chưa đăng nhập'}</strong>
          </div>
          <div>
            <span>Hạn dùng</span>
            <strong>{formatShortDate(account.expires_at)}</strong>
            <small className={`expiry-countdown-pill ${getDaysLeftClass(account.expires_at)}`}>
              {daysLeft(account.expires_at) !== null
                ? daysLeft(account.expires_at) < 0
                  ? 'Đã hết hạn'
                  : `Còn ${daysLeft(account.expires_at)} ngày`
                : 'Vô hạn'}
            </small>
          </div>
          {isStudent && (
            <div>
              <span>Lượt thi</span>
              <strong>{quotaUnlimited ? 'Vô hạn' : `${account.exam_used || 0}/${account.exam_quota}`}</strong>
              <div className="quota-bar-track">
                <div
                  className={`quota-bar-fill ${quotaUnlimited ? 'unlimited' : quotaPercent >= 100 ? 'empty' : 'normal'}`}
                  style={{ width: `${quotaPercent}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="mobile-account-actions">{renderAccountActions(account, isStudent)}</div>
      </article>
    );
  };

  return (
    <div className="admin-unified-app-layout">
      {/* 1. LEFT SIDEBAR NAVIGATION */}
      <aside className="admin-unified-sidebar">
        <Link to="/admin/accounts" className="sidebar-brand-box" title="Trung tâm Đào tạo Tin học Sao Việt">
          <div className="sidebar-logo-icon">
            <img src="/brand/sao-viet-mark.png" alt="Logo Sao Việt" className="sidebar-brand-mark-img" />
          </div>
          <div className="sidebar-brand-text">
            <strong>Sao Việt</strong>
            <span>Ôn thi chứng chỉ GenAI</span>
          </div>
        </Link>

        <nav className="sidebar-nav-menu">
          <Link to="/admin/accounts" className="sidebar-nav-item active" data-short="Tài khoản">
            <UserGroupIcon className="w-5 h-5" />
            <span>Quản lý tài khoản</span>
          </Link>
        </nav>

        <div className="sidebar-footer">
          <Link to="/" className="sidebar-nav-item footer-link">
            <QuestionMarkCircleIcon className="w-5 h-5" />
            <span>Cổng ôn thi</span>
          </Link>
          <button onClick={logout} type="button" className="sidebar-nav-item footer-link logout-btn">
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
            <span>Đăng xuất</span>
          </button>
        </div>
      </aside>

      {/* 2. MAIN VIEW CONTENT */}
      <div className="admin-unified-body">
        {/* TOPBAR */}
        <header className="admin-unified-topbar">
          <div className="topbar-breadcrumbs">
            <Link to="/admin/accounts" className="crumb-home" title="Quản lý tài khoản">
              <HomeIcon className="w-4 h-4" />
            </Link>
            <ChevronRightIcon className="w-3.5 h-3.5 text-slate-400" />
            <span className="crumb-active">Quản lý tài khoản</span>
          </div>

          <div className="topbar-right-meta">
            <Link
              to="/"
              className="btn-back-to-portal"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                borderRadius: 7,
                background: '#eff6ff',
                color: '#1d4ed8',
                border: '1px solid #bfdbfe',
                fontSize: 13,
                fontWeight: 650,
                textDecoration: 'none',
                transition: 'all 0.15s ease',
              }}
              title="Quay lại Cổng ôn luyện và thi thử của học viên"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              <span>Về Cổng ôn thi</span>
            </Link>

            <button
              type="button"
              onClick={handleOpenAuditLog}
              className="btn-audit-log-topbar"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 7,
                background: '#f8fafc',
                color: '#475569',
                border: '1px solid #cbd5e1',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              title="Xem nhật ký lịch sử các thao tác xóa, gia hạn và bảo mật"
            >
              <ClockIcon className="w-4 h-4 text-slate-500" />
              <span>Nhật ký</span>
            </button>

            <NotificationBell notifications={importantNotifications} />

            <div className="topbar-user-profile">
              <div className="user-avatar-circle dicebear-avatar-shell topbar-avatar-shell">
                <img
                  alt=""
                  className="dicebear-avatar-img"
                  src={buildDiceBearAvatarUrl(currentUser)}
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                    event.currentTarget.nextElementSibling.style.display = 'inline';
                  }}
                />
                <span className="avatar-fallback-initial">
                  {getInitials(currentUser?.full_name, currentUser?.email || currentUser?.username)}
                </span>
              </div>
              <div className="user-meta-text">
                <strong>{currentUser?.full_name || 'Admin Sao Việt'}</strong>
                <span>{currentUser?.role === 'center' ? 'Quản trị trung tâm' : 'Quản trị hệ thống'}</span>
              </div>
            </div>

            <div className="topbar-datetime">
              <CalendarDaysIcon className="w-4 h-4 text-slate-400 inline mr-1.5" />
              <span>{formatHeaderDateTime()}</span>
            </div>
          </div>
        </header>

        {/* PAGE CONTENT CONTAINER */}
        <main className="admin-content-container">
          {/* TITLE & HERO HEADER */}
          <div className="admin-page-hero">
            <div className="page-hero-title-group">
              <div className="page-hero-icon-box">
                <BuildingOffice2Icon className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2>Quản trị Cấp tài khoản & Quản lý Hạn mức Lượt thi</h2>
                <p>Quản lý toàn bộ tài khoản học viên và trung tâm đối tác trên hệ thống Ôn thi Chứng chỉ GenAI Sao Việt.</p>
              </div>
            </div>

            <button onClick={loadData} type="button" className="btn-refresh-data" title="Làm mới dữ liệu">
              <ArrowPathIcon className="w-4 h-4" /> Làm mới
            </button>
          </div>

          {/* ALERTS */}
          {error && (
            <div className="account-alert error">{typeof error === 'string' ? error : JSON.stringify(error)}</div>
          )}
          {notice && (
            <div className="account-alert success">
              <CheckCircleIcon className="w-5 h-5" /> {notice}
            </div>
          )}

          {/* 5 METRIC STAT CARDS */}
          <div className="admin-metric-cards-5">
            {/* 1. Trung tâm */}
            <article
              className={`metric-card theme-blue clickable-metric-card ${roleFilter === 'center' && filterTab === 'all' ? 'active-metric-filter' : ''}`}
              onClick={() => {
                if (!isAdmin) return;
                if (roleFilter === 'center' && filterTab === 'all') {
                  setRoleFilter('all');
                } else {
                  setRoleFilter('center');
                  setFilterTab('all');
                }
              }}
              role={isAdmin ? 'button' : undefined}
              tabIndex={isAdmin ? 0 : undefined}
              title={isAdmin ? 'Bấm để lọc danh sách Trung tâm đối tác' : undefined}
              style={{
                cursor: isAdmin ? 'pointer' : 'default',
                borderColor: roleFilter === 'center' && filterTab === 'all' ? '#2563eb' : undefined,
                boxShadow: roleFilter === 'center' && filterTab === 'all' ? '0 0 0 2px rgba(37, 99, 235, 0.25)' : undefined,
                background: roleFilter === 'center' && filterTab === 'all' ? '#f8fbff' : undefined,
              }}
            >
              <div className="metric-icon-wrap">
                <BuildingOffice2Icon className="w-6 h-6" />
              </div>
              <div className="metric-content">
                <div className="metric-num-row">
                  <strong>{summary?.total_centers || centers.length || 0}</strong>
                  <span className="metric-badge-tag green">
                    {roleFilter === 'center' && filterTab === 'all' ? 'Đang lọc ✓' : `↑ +${centers.length || 1}`}
                  </span>
                </div>
                <span>Trung tâm đối tác</span>
              </div>
            </article>

            {/* 2. Tổng học viên */}
            <article
              className={`metric-card theme-purple clickable-metric-card ${roleFilter === 'student' && filterTab === 'all' ? 'active-metric-filter' : ''}`}
              onClick={() => {
                if (roleFilter === 'student' && filterTab === 'all') {
                  setRoleFilter('all');
                } else {
                  setRoleFilter('student');
                  setFilterTab('all');
                }
              }}
              role="button"
              tabIndex={0}
              title="Bấm để lọc danh sách Tổng học viên"
              style={{
                cursor: 'pointer',
                borderColor: roleFilter === 'student' && filterTab === 'all' ? '#9333ea' : undefined,
                boxShadow: roleFilter === 'student' && filterTab === 'all' ? '0 0 0 2px rgba(147, 51, 234, 0.25)' : undefined,
                background: roleFilter === 'student' && filterTab === 'all' ? '#fdfaff' : undefined,
              }}
            >
              <div className="metric-icon-wrap">
                <UserGroupIcon className="w-6 h-6" />
              </div>
              <div className="metric-content">
                <div className="metric-num-row">
                  <strong>
                    {(summary?.total_students ?? accounts.filter((a) => a.role === 'student').length).toLocaleString()}
                  </strong>
                  <span className="metric-badge-tag green">
                    {roleFilter === 'student' && filterTab === 'all' ? 'Đang lọc ✓' : '↑ +12%'}
                  </span>
                </div>
                <span>Tổng học viên</span>
              </div>
            </article>

            {/* 3. Đã kích hoạt */}
            <article
              className={`metric-card theme-green clickable-metric-card ${filterTab === 'activated' ? 'active-metric-filter' : ''}`}
              onClick={() => {
                if (filterTab === 'activated') {
                  setFilterTab('all');
                  setRoleFilter('all');
                } else {
                  setFilterTab('activated');
                  setRoleFilter('student');
                }
              }}
              role="button"
              tabIndex={0}
              title="Bấm để lọc danh sách Học viên đã kích hoạt"
              style={{
                cursor: 'pointer',
                borderColor: filterTab === 'activated' ? '#16a34a' : undefined,
                boxShadow: filterTab === 'activated' ? '0 0 0 2px rgba(22, 163, 74, 0.25)' : undefined,
                background: filterTab === 'activated' ? '#f7fee7' : undefined,
              }}
            >
              <div className="metric-icon-wrap">
                <CheckCircleIcon className="w-6 h-6" />
              </div>
              <div className="metric-content">
                <div className="metric-num-row">
                  <strong>{activatedAccounts.length.toLocaleString()}</strong>
                  <span className="metric-badge-tag green">
                    {filterTab === 'activated'
                      ? 'Đang lọc ✓'
                      : accounts.length
                        ? `${Math.round((activatedAccounts.length * 100) / accounts.length)}%`
                        : '0%'}
                  </span>
                </div>
                <span>Đã kích hoạt</span>
              </div>
            </article>

            {/* 4. Hết lượt thi */}
            <article
              className={`metric-card theme-orange clickable-metric-card ${filterTab === 'quota_exceeded' ? 'active-metric-filter' : ''}`}
              onClick={() => {
                if (filterTab === 'quota_exceeded') {
                  setFilterTab('all');
                  setRoleFilter('all');
                } else {
                  setFilterTab('quota_exceeded');
                  setRoleFilter('student');
                }
              }}
              role="button"
              tabIndex={0}
              title="Bấm để lọc danh sách Học viên hết lượt thi"
              style={{
                cursor: 'pointer',
                borderColor: filterTab === 'quota_exceeded' ? '#ea580c' : undefined,
                boxShadow: filterTab === 'quota_exceeded' ? '0 0 0 2px rgba(234, 88, 12, 0.25)' : undefined,
                background: filterTab === 'quota_exceeded' ? '#fffaf5' : undefined,
              }}
            >
              <div className="metric-icon-wrap">
                <ClockIcon className="w-6 h-6" />
              </div>
              <div className="metric-content">
                <div className="metric-num-row">
                  <strong>{quotaExceededAccounts.length.toLocaleString()}</strong>
                  <span className="metric-badge-tag orange">
                    {filterTab === 'quota_exceeded'
                      ? 'Đang lọc ✓'
                      : accounts.length
                        ? `${Math.round((quotaExceededAccounts.length * 100) / accounts.length)}%`
                        : '0%'}
                  </span>
                </div>
                <span>Hết lượt thi</span>
              </div>
            </article>

            {/* 5. Chưa đăng nhập */}
            <article
              className={`metric-card theme-red clickable-metric-card ${filterTab === 'pending' ? 'active-metric-filter' : ''}`}
              onClick={() => {
                if (filterTab === 'pending') {
                  setFilterTab('all');
                  setRoleFilter('all');
                } else {
                  setFilterTab('pending');
                  setRoleFilter('student');
                }
              }}
              role="button"
              tabIndex={0}
              title="Bấm để lọc danh sách Học viên chưa đăng nhập"
              style={{
                cursor: 'pointer',
                borderColor: filterTab === 'pending' ? '#ef4444' : undefined,
                boxShadow: filterTab === 'pending' ? '0 0 0 2px rgba(239, 68, 68, 0.25)' : undefined,
                background: filterTab === 'pending' ? '#fffafa' : undefined,
              }}
            >
              <div className="metric-icon-wrap">
                <CalendarDaysIcon className="w-6 h-6" />
              </div>
              <div className="metric-content">
                <div className="metric-num-row">
                  <strong>{pendingAccounts.length.toLocaleString()}</strong>
                  <span className="metric-badge-tag red">
                    {filterTab === 'pending'
                      ? 'Đang lọc ✓'
                      : accounts.length
                        ? `${Math.round((pendingAccounts.length * 100) / accounts.length)}%`
                        : '0%'}
                  </span>
                </div>
                <span>Chưa đăng nhập</span>
              </div>
            </article>
          </div>

          {/* TWO-COLUMN GRID: TABLE (LEFT 70%) & ACCOUNT CREATION (RIGHT 30%) */}
          <div className="admin-main-two-columns">
            {/* LEFT COLUMN: TABLE & FILTERS */}
            <section className="admin-table-section-card">
              {/* FILTER PILLS */}
              <div className="admin-filter-bar">
                <div className="filter-pills-scroll">
                  <button
                    className={`filter-pill-chip ${filterTab === 'all' && roleFilter === 'all' && centerFilter === 'all' && classroomFilter === 'all' ? 'active' : ''}`}
                    onClick={() => {
                      setFilterTab('all');
                      setRoleFilter('all');
                      setCenterFilter('all');
                      setClassroomFilter('all');
                    }}
                    type="button"
                  >
                    Tất cả ({accounts.length})
                  </button>
                  <button
                    className={`filter-pill-chip ${filterTab === 'active' ? 'active' : ''}`}
                    onClick={() => setFilterTab('active')}
                    type="button"
                  >
                    Đang hoạt động ({accounts.filter((a) => getStatus(a) === 'active').length})
                  </button>
                  <button
                    className={`filter-pill-chip ${filterTab === 'activated' ? 'active' : ''}`}
                    onClick={() => setFilterTab('activated')}
                    type="button"
                  >
                    Đã kích hoạt ({activatedAccounts.length})
                  </button>
                  <button
                    className={`filter-pill-chip ${filterTab === 'pending' ? 'active' : ''}`}
                    onClick={() => setFilterTab('pending')}
                    type="button"
                  >
                    Chưa đăng nhập ({pendingAccounts.length})
                  </button>
                  <button
                    className={`filter-pill-chip ${filterTab === 'quota_exceeded' ? 'active' : ''}`}
                    onClick={() => setFilterTab('quota_exceeded')}
                    type="button"
                  >
                    Hết lượt thi ({quotaExceededAccounts.length})
                  </button>
                  <button
                    className={`filter-pill-chip ${filterTab === 'expiring' ? 'active' : ''}`}
                    onClick={() => setFilterTab('expiring')}
                    type="button"
                  >
                    Sắp hết hạn ({expiringSoon.length})
                  </button>
                  <button
                    className={`filter-pill-chip ${filterTab === 'expired' ? 'active' : ''}`}
                    onClick={() => setFilterTab('expired')}
                    type="button"
                  >
                    Hết hạn ngày ({expiredAccounts.length})
                  </button>
                  {lockedAccounts.length > 0 && (
                    <button
                      className={`filter-pill-chip ${filterTab === 'locked' ? 'active' : ''}`}
                      onClick={() => setFilterTab('locked')}
                      type="button"
                    >
                      Đã khóa ({lockedAccounts.length})
                    </button>
                  )}
                </div>

                {/* SEARCH & DIRECT FILTERS */}
                <div className="search-and-advanced-row">
                  <div className="search-input-wrapper">
                    <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 search-icon" />
                    <input
                      type="text"
                      placeholder="Tìm theo họ tên, username, SĐT, email, lớp học..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="search-input-field"
                    />
                    {searchQuery && (
                      <button onClick={() => setSearchQuery('')} className="search-clear-btn" type="button">
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="search-actions-group">
                    {isAdmin ? (
                      <>
                        <div
                          className="inline-role-filter-wrap"
                          style={{
                            position: 'relative',
                            display: 'inline-flex',
                            alignItems: 'center',
                          }}
                        >
                          <FunnelIcon
                            className="w-4 h-4"
                            style={{
                              position: 'absolute',
                              left: '11px',
                              pointerEvents: 'none',
                              color: roleFilter !== 'all' ? '#2563eb' : '#64748b',
                            }}
                          />
                          <select
                            value={roleFilter}
                            onChange={(e) => setRoleFilter(e.target.value)}
                            title="Lọc nhanh theo phân cấp vai trò"
                            style={{
                              height: '38px',
                              padding: '0 26px 0 32px',
                              borderRadius: '8px',
                              border: roleFilter !== 'all' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                              background: roleFilter !== 'all' ? '#eff6ff' : '#ffffff',
                              color: roleFilter !== 'all' ? '#1d4ed8' : '#334155',
                              fontSize: '12.5px',
                              fontWeight: 650,
                              cursor: 'pointer',
                              outline: 'none',
                            }}
                          >
                            <option value="all">Vai trò: Tất cả ({accounts.length})</option>
                            <option value="student">
                              Chỉ Học viên ({accounts.filter((a) => a.role === 'student').length})
                            </option>
                            <option value="center">Chỉ Trung tâm ({centers.length})</option>
                            <option value="admin">Chỉ Quản trị viên ({adminAccounts.length})</option>
                          </select>
                        </div>

                        <div
                          className="inline-role-filter-wrap"
                          style={{
                            position: 'relative',
                            display: 'inline-flex',
                            alignItems: 'center',
                          }}
                        >
                          <BuildingOffice2Icon
                            className="w-4 h-4"
                            style={{
                              position: 'absolute',
                              left: '11px',
                              pointerEvents: 'none',
                              color: centerFilter !== 'all' ? '#2563eb' : '#64748b',
                            }}
                          />
                          <select
                            value={centerFilter}
                            onChange={(e) => {
                              setCenterFilter(e.target.value);
                              setClassroomFilter('all');
                            }}
                            title="Lọc học viên theo Trung tâm đối tác"
                            style={{
                              height: '38px',
                              padding: '0 26px 0 32px',
                              maxWidth: '220px',
                              borderRadius: '8px',
                              border: centerFilter !== 'all' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                              background: centerFilter !== 'all' ? '#eff6ff' : '#ffffff',
                              color: centerFilter !== 'all' ? '#1d4ed8' : '#334155',
                              fontSize: '12.5px',
                              fontWeight: 650,
                              cursor: 'pointer',
                              outline: 'none',
                            }}
                          >
                            <option value="all">Trung tâm: Tất cả ({centers.length})</option>
                            <option value="none">
                              Học viên tự do ({accounts.filter((a) => a.role === 'student' && !a.center_id).length})
                            </option>
                            {centers.map((c) => {
                              const count = accounts.filter(
                                (a) => a.role === 'student' && String(a.center_id) === String(c.id),
                              ).length;
                              return (
                                <option key={c.id} value={String(c.id)}>
                                  {c.full_name || c.email} ({count} HV)
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      </>
                    ) : (
                      <div
                        className="inline-role-filter-wrap"
                        style={{
                          position: 'relative',
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                      >
                        <FunnelIcon
                          className="w-4 h-4"
                          style={{
                            position: 'absolute',
                            left: '11px',
                            pointerEvents: 'none',
                            color: filterTab !== 'all' ? '#2563eb' : '#64748b',
                          }}
                        />
                        <select
                          value={filterTab}
                          onChange={(e) => setFilterTab(e.target.value)}
                          title="Lọc nhanh học viên theo trạng thái"
                          style={{
                            height: '38px',
                            padding: '0 26px 0 32px',
                            borderRadius: '8px',
                            border: filterTab !== 'all' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                            background: filterTab !== 'all' ? '#eff6ff' : '#ffffff',
                            color: filterTab !== 'all' ? '#1d4ed8' : '#334155',
                            fontSize: '12.5px',
                            fontWeight: 650,
                            cursor: 'pointer',
                            outline: 'none',
                          }}
                        >
                          <option value="all">Bộ lọc: Tất cả trạng thái ({accounts.length})</option>
                          <option value="active">
                            Đang hoạt động ({accounts.filter((a) => getStatus(a) === 'active').length})
                          </option>
                          <option value="activated">Đã kích hoạt ({activatedAccounts.length})</option>
                          <option value="pending">Chưa đăng nhập ({pendingAccounts.length})</option>
                          <option value="quota_exceeded">Hết lượt thi ({quotaExceededAccounts.length})</option>
                          <option value="expiring">Sắp hết hạn ({expiringSoon.length})</option>
                          <option value="expired">Hết hạn ngày ({expiredAccounts.length})</option>
                          <option value="locked">Đã khóa ({lockedAccounts.length})</option>
                        </select>
                      </div>
                    )}

                    {/* CLASSROOM FILTER FOR BOTH CENTER & ADMIN */}
                    <div
                      className="inline-role-filter-wrap"
                      style={{
                        position: 'relative',
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      <AcademicCapIcon
                        className="w-4 h-4"
                        style={{
                          position: 'absolute',
                          left: '11px',
                          pointerEvents: 'none',
                          color: classroomFilter !== 'all' ? '#2563eb' : '#64748b',
                        }}
                      />
                      <select
                        value={classroomFilter}
                        onChange={(e) => setClassroomFilter(e.target.value)}
                        title="Lọc học viên theo Lớp học"
                        style={{
                          height: '38px',
                          padding: '0 26px 0 32px',
                          maxWidth: '210px',
                          borderRadius: '8px',
                          border: classroomFilter !== 'all' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                          background: classroomFilter !== 'all' ? '#eff6ff' : '#ffffff',
                          color: classroomFilter !== 'all' ? '#1d4ed8' : '#334155',
                          fontSize: '12.5px',
                          fontWeight: 650,
                          cursor: 'pointer',
                          outline: 'none',
                        }}
                      >
                        <option value="all">Lớp học: Tất cả ({filterClassroomOptions.length} lớp)</option>
                        <option value="none">
                          Chưa phân lớp ({accounts.filter((a) => a.role === 'student' && !a.classroom_id).length})
                        </option>
                        {filterClassroomOptions.map((cls) => {
                          const count = accounts.filter(
                            (a) => a.role === 'student' && String(a.classroom_id) === String(cls.id),
                          ).length;
                          return (
                            <option key={cls.id} value={String(cls.id)}>
                              {cls.code ? `${cls.code} - ` : ''}{cls.name} ({count} HV)
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <button
                      className="btn-export-excel"
                      type="button"
                      onClick={handleExportFilteredAccounts}
                      title="Xem & Xuất file TXT hoặc sao chép danh sách tài khoản & mật khẩu"
                    >
                      <DocumentTextIcon className="w-4 h-4 text-blue-600" />
                      <span>
                        {selectedIds.length > 0
                          ? `Xuất ${selectedIds.length} mục chọn`
                          : `Xuất danh sách TXT (${filteredAccounts.length})`}
                      </span>
                    </button>

                    <button
                      className="btn-copy-table"
                      type="button"
                      onClick={handleCopyFilteredAccounts}
                      title="Sao chép nhanh danh sách (kèm mật khẩu) vào Clipboard để dán trực tiếp vào Excel / Google Sheets"
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                    </button>

                    <button
                      className="btn-bulk-delete-action"
                      type="button"
                      onClick={handleOpenBulkDeleteModal}
                      disabled={deletableTargetAccounts.length === 0}
                      title={
                        selectedIds.length > 0
                          ? `Xóa ${deletableTargetAccounts.length} tài khoản học viên đang được tích chọn`
                          : `Xóa nhanh ${deletableTargetAccounts.length} tài khoản học viên theo bộ lọc hiện tại`
                      }
                      style={{
                        alignItems: 'center',
                        background: deletableTargetAccounts.length > 0 ? '#fff1f2' : '#f8fafc',
                        border: deletableTargetAccounts.length > 0 ? '1px solid #fecdd3' : '1px solid #e2e8f0',
                        borderRadius: '7px',
                        color: deletableTargetAccounts.length > 0 ? '#e11d48' : '#94a3b8',
                        cursor: deletableTargetAccounts.length > 0 ? 'pointer' : 'not-allowed',
                        display: 'inline-flex',
                        fontSize: '13px',
                        fontWeight: 650,
                        gap: '6px',
                        padding: '8px 13px',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <TrashIcon className="w-4 h-4" />
                      <span>
                        {selectedIds.length > 0
                          ? `Xóa ${deletableTargetAccounts.length} đã chọn`
                          : `Xóa theo bộ lọc (${deletableTargetAccounts.length})`}
                      </span>
                    </button>

                    {(searchQuery || filterTab !== 'all' || roleFilter !== 'all' || centerFilter !== 'all' || classroomFilter !== 'all') && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchQuery('');
                          setFilterTab('all');
                          setRoleFilter('all');
                          setCenterFilter('all');
                          setClassroomFilter('all');
                          setSelectedIds([]);
                        }}
                        className="btn-reset-filters"
                        title="Đặt lại tìm kiếm và các bộ lọc về mặc định"
                        style={{
                          alignItems: 'center',
                          background: '#f8fafc',
                          border: '1px solid #cbd5e1',
                          borderRadius: '7px',
                          color: '#475569',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          fontSize: '13px',
                          fontWeight: 600,
                          gap: '5px',
                          padding: '8px 12px',
                        }}
                      >
                        <ArrowPathIcon className="w-4 h-4 text-slate-500" />
                        <span>Đặt lại bộ lọc</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* DATA TABLE */}
              {loading ? (
                <div className="account-loading-state">
                  <ArrowPathIcon className="w-6 h-6 animate-spin text-blue-600" />
                  <span>Đang tải danh sách tài khoản...</span>
                </div>
              ) : (
                <div className="admin-table-responsive-wrapper">
                  <table className="admin-data-table">
                    <thead>
                      <tr>
                        <th className="th-checkbox">
                          <input
                            type="checkbox"
                            checked={
                              paginatedAccounts.length > 0 &&
                              paginatedAccounts.every((a) => selectedIds.includes(a.id))
                            }
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th>Tài khoản</th>
                        <th>Phân cấp</th>
                        <th>Kích hoạt & Đăng nhập</th>
                        <th>Lượt thi & Luyện</th>
                        <th>Hạn dùng</th>
                        <th>Trạng thái</th>
                        <th>Thao tác</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedAccounts.map((account) => {
                        const isStudent = account.role === 'student';
                        const avatarStyle = getAvatarBg(account.full_name || account.email);
                        const isSelected = selectedIds.includes(account.id);

                        return (
                          <tr key={account.id} className={isSelected ? 'row-selected' : ''}>
                            <td className="td-checkbox">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleSelect(account.id)}
                              />
                            </td>
                            <td>
                              <div
                                className="account-user-profile-cell clickable-profile"
                                onClick={() => setDetailDrawerAccount(account)}
                                style={{ cursor: 'pointer' }}
                                title="Bấm để xem hồ sơ chi tiết và thao tác nhanh"
                              >
                                <div
                                  className="user-initial-avatar dicebear-avatar-shell"
                                  style={{ background: avatarStyle.bg, color: avatarStyle.color }}
                                >
                                  <img
                                    alt=""
                                    className="dicebear-avatar-img"
                                    src={buildDiceBearAvatarUrl(account)}
                                    onError={(event) => {
                                      event.currentTarget.style.display = 'none';
                                      event.currentTarget.nextElementSibling.style.display = 'inline';
                                    }}
                                  />
                                  <span className="avatar-fallback-initial">
                                    {getInitials(account.full_name, account.email)}
                                  </span>
                                </div>
                                <div className="user-profile-details">
                                  <strong className="user-full-name">
                                    {account.full_name || (isStudent ? `Học viên ${account.id}` : roleLabel(account.role))}
                                  </strong>
                                  <span className="user-login-meta">
                                    {account.username || account.email}
                                    {(account.phone || account.phone_number) ? ` | ${account.phone || account.phone_number}` : ''}
                                  </span>
                                  {(account.initial_password || account.password) && (
                                    <span
                                      className="user-password-meta"
                                      title="Mật khẩu ban đầu của học viên"
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        fontSize: '11px',
                                        color: '#047857',
                                        background: '#ecfdf5',
                                        border: '1px solid #a7f3d0',
                                        borderRadius: '4px',
                                        padding: '1px 6px',
                                        marginTop: '2px',
                                        marginBottom: '2px',
                                        width: 'fit-content',
                                        fontWeight: 600,
                                        fontFamily: 'monospace',
                                      }}
                                    >
                                      🔑 {account.initial_password || account.password}
                                    </span>
                                  )}
                                  {(account.classroom_name || account.classroom_code) && (
                                    <span className={`classroom-student-tag ${account.is_classroom_expired ? 'expired-tag' : ''}`}>
                                      📚 Lớp: {account.classroom_code ? `${account.classroom_code} - ` : ''}{account.classroom_name || account.classroom_code}
                                      {account.classroom_ends_at && ` (Hạn: ${formatShortDate(account.classroom_ends_at)})`}
                                    </span>
                                  )}
                                  <span className="user-email-meta">{account.email}</span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span
                                className={`role-pill-badge ${
                                  isAdminRole(account.role)
                                    ? 'admin'
                                    : account.role === 'center'
                                      ? 'center'
                                      : 'student'
                                }`}
                              >
                                {roleLabel(account.role)}
                              </span>
                            </td>
                            <td>
                              {isStudent ? (
                                <div className="activation-cell">
                                  {account.first_login_completed ? (
                                    <>
                                      <span className="activation-pill-tag activated">Đã kích hoạt</span>
                                      <span className="activation-timestamp">
                                        {formatDateTime(account.first_login_at || account.last_login_at)}
                                      </span>
                                    </>
                                  ) : (
                                    <span className="activation-pill-tag pending">Chưa đăng nhập</span>
                                  )}
                                </div>
                              ) : (
                                <span className="activation-pill-tag admin">Đã kích hoạt</span>
                              )}
                            </td>
                            <td>
                              {isStudent ? (
                                <div className="quota-bar-cell">
                                  <div className="quota-num-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                      {account.exam_quota === 0 || account.exam_quota === null ? (
                                        <span>Thi: <strong className="text-emerald-600">Vô hạn</strong></span>
                                      ) : (
                                        <span>
                                          Thi: <strong>{account.exam_used || 0}</strong>/{account.exam_quota}
                                        </span>
                                      )}
                                      <button
                                        type="button"
                                        className="quota-inline-add-btn"
                                        title="Cấp thêm lượt thi thử"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openQuotaModal(account);
                                        }}
                                      >
                                        +
                                      </button>
                                    </span>
                                    <span
                                      title="Số lần luyện tập tự do"
                                      style={{
                                        fontSize: '11px',
                                        color: '#6b21a8',
                                        background: '#f3e8ff',
                                        padding: '1px 6px',
                                        borderRadius: '4px',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      Luyện: {account.practice_used || 0}
                                    </span>
                                  </div>
                                  <div className="quota-bar-track">
                                    {account.exam_quota === 0 || account.exam_quota === null ? (
                                      <div className="quota-bar-fill unlimited" style={{ width: '100%' }} />
                                    ) : (
                                      <div
                                        className={`quota-bar-fill ${
                                          (account.exam_used || 0) >= account.exam_quota ? 'empty' : 'normal'
                                        }`}
                                        style={{
                                          width: `${Math.min(
                                            100,
                                            Math.round(((account.exam_used || 0) * 100) / account.exam_quota),
                                          )}%`,
                                        }}
                                      />
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>
                              <div className="expiry-cell">
                                <strong>{formatShortDate(account.expires_at)}</strong>
                                <span className={`expiry-countdown-pill ${getDaysLeftClass(account.expires_at)}`}>
                                  {daysLeft(account.expires_at) !== null
                                    ? daysLeft(account.expires_at) < 0
                                      ? 'Đã hết hạn'
                                      : `Còn ${daysLeft(account.expires_at)} ngày`
                                    : 'Vô hạn'}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span className={`account-status-badge ${account.is_classroom_expired ? 'classroom_expired' : getStatus(account)}`}>
                                {statusLabel(account)}
                              </span>
                            </td>

                            <td>
                              {renderAccountActions(account, isStudent)}
                            </td>
                          </tr>
                        );
                      })}
                      {filteredAccounts.length === 0 && (
                        <tr>
                          <td colSpan="8">
                            <div className="account-empty-state">
                              <UserGroupIcon className="w-10 h-10 text-slate-300" />
                              <p>Không tìm thấy tài khoản nào phù hợp với bộ lọc hiện tại.</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {!loading && (
                <div className="mobile-account-list">
                  {paginatedAccounts.length > 0 ? (
                    paginatedAccounts.map((account) => renderMobileAccountCard(account))
                  ) : (
                    <div className="account-empty-state mobile-empty-state">
                      <UserGroupIcon className="w-10 h-10 text-slate-300" />
                      <p>Không tìm thấy tài khoản nào phù hợp với bộ lọc hiện tại.</p>
                    </div>
                  )}
                </div>
              )}

              {/* TABLE PAGINATION FOOTER */}
              <div className="admin-table-pagination-footer">
                <div className="pagination-info">
                  Hiển thị {filteredAccounts.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} -{' '}
                  {Math.min(currentPage * pageSize, filteredAccounts.length)} của {filteredAccounts.length.toLocaleString()}{' '}
                  tài khoản
                </div>

                <div className="pagination-controls">
                  <button
                    className="page-btn-arrow"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    type="button"
                  >
                    &lt;
                  </button>
                  {pageNumbers.map((num, idx) => (
                    <button
                      key={idx}
                      className={`page-btn-num ${num === currentPage ? 'active' : ''} ${
                        num === '...' ? 'dots' : ''
                      }`}
                      disabled={num === '...'}
                      onClick={() => typeof num === 'number' && setCurrentPage(num)}
                      type="button"
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    className="page-btn-arrow"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    type="button"
                  >
                    &gt;
                  </button>
                </div>

                <div className="pagination-page-size">
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="page-size-select"
                  >
                    <option value={8}>Hiển thị 8 / trang</option>
                    <option value={15}>Hiển thị 15 / trang</option>
                    <option value={30}>Hiển thị 30 / trang</option>
                    <option value={50}>Hiển thị 50 / trang</option>
                  </select>
                </div>
              </div>

              {/* FLOATING SAFE BULK ACTION BAR */}
              {selectedIds.length > 0 && (
                <div className="floating-bulk-action-dock" role="region" aria-label="Thao tác hàng loạt">
                  <div className="floating-bulk-inner">
                    <div className="floating-bulk-counter">
                      <span className="bulk-badge-num">{selectedIds.length}</span>
                      <span className="bulk-badge-label">đã chọn</span>
                    </div>

                    <div className="floating-bulk-separator" />

                    <div className="floating-bulk-buttons">
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => handleBulkExtendMonths(1)}
                        className="bulk-dock-btn"
                        title="Gia hạn thêm 1 tháng cho tất cả tài khoản đã chọn"
                      >
                        <CalendarDaysIcon className="w-4 h-4 text-blue-500" />
                        <span>+1 Tháng</span>
                      </button>

                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => handleBulkExtendMonths(3)}
                        className="bulk-dock-btn"
                        title="Gia hạn thêm 3 tháng cho tất cả tài khoản đã chọn"
                      >
                        <CalendarDaysIcon className="w-4 h-4 text-indigo-500" />
                        <span>+3 Tháng</span>
                      </button>

                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => handleBulkExtendMonths(6)}
                        className="bulk-dock-btn"
                        title="Gia hạn thêm 6 tháng cho tất cả tài khoản đã chọn"
                      >
                        <CalendarDaysIcon className="w-4 h-4 text-violet-500" />
                        <span>+6 Tháng</span>
                      </button>

                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => handleBulkExtendMonths(12)}
                        className="bulk-dock-btn"
                        title="Gia hạn thêm 1 năm (12 tháng) cho tất cả tài khoản đã chọn"
                      >
                        <CalendarDaysIcon className="w-4 h-4 text-purple-600" />
                        <span>+1 Năm</span>
                      </button>

                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => handleBulkAddQuota(5)}
                        className="bulk-dock-btn"
                        title="Cộng thêm 5 lượt thi cho các tài khoản học viên đã chọn"
                      >
                        <PlusIcon className="w-4 h-4 text-emerald-500" />
                        <span>+5 Lượt thi</span>
                      </button>

                      <button
                        type="button"
                        disabled={saving}
                        onClick={handleBulkToggleLock}
                        className="bulk-dock-btn"
                        title="Đổi trạng thái Khóa / Mở khóa cho các tài khoản đã chọn"
                      >
                        <LockClosedIcon className="w-4 h-4 text-amber-500" />
                        <span>Khóa / Mở</span>
                      </button>

                      <button
                        type="button"
                        onClick={handleExportFilteredAccounts}
                        className="bulk-dock-btn"
                        title="Xuất file TXT hoặc sao chép danh sách tài khoản đã chọn"
                      >
                        <DocumentTextIcon className="w-4 h-4 text-sky-500" />
                        <span>Xuất TXT</span>
                      </button>

                      <button
                        type="button"
                        disabled={saving}
                        onClick={handleOpenBulkDeleteModal}
                        className="bulk-dock-btn delete"
                        title="Xóa vĩnh viễn các tài khoản đã chọn với xác nhận gõ chữ XÓA"
                      >
                        <TrashIcon className="w-4 h-4 text-rose-500" />
                        <span>Xóa đã chọn</span>
                      </button>
                    </div>

                    <div className="floating-bulk-separator" />

                    <button
                      type="button"
                      onClick={() => setSelectedIds([])}
                      className="bulk-dock-clear-btn"
                      title="Bỏ chọn tất cả"
                    >
                      <XMarkIcon className="w-4 h-4" />
                      <span>Bỏ chọn</span>
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* RIGHT COLUMN: ACTION PANEL - CẤP TÀI KHOẢN MỚI */}
            <aside className="admin-create-action-panel">
              <div className="create-panel-header-badge">
                <div className="header-badge-icon purple">
                  <UserGroupIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3>Cấp tài khoản mới</h3>
                  <p>
                    {isAdmin
                      ? 'Tạo tài khoản Học viên, Trung tâm đối tác hoặc Quản trị viên'
                      : 'Tạo học viên thuộc trung tâm đang đăng nhập'}
                  </p>
                </div>
              </div>

              {/* ROLE SWITCHER */}
              <div
                className="role-switcher-segmented"
                style={isAdmin ? { gridTemplateColumns: '1fr 1fr 1fr' } : undefined}
              >
                <button
                  className={`role-seg-btn ${accountType === 'student' ? 'active' : ''}`}
                  onClick={() => setAccountType('student')}
                  type="button"
                >
                  Học viên
                </button>
                {isAdmin && (
                  <button
                    className={`role-seg-btn ${accountType === 'center' ? 'active' : ''}`}
                    onClick={() => setAccountType('center')}
                    type="button"
                  >
                    Trung tâm
                  </button>
                )}
                {isAdmin && (
                  <button
                    className={`role-seg-btn ${accountType === 'admin' ? 'active' : ''}`}
                    onClick={() => setAccountType('admin')}
                    type="button"
                  >
                    🛡️ Quản trị
                  </button>
                )}
              </div>

              {/* SUB TABS (FOR STUDENT) */}
              {accountType === 'student' && (
                <div className="sub-methods-pill-bar">
                  <button
                    className={`sub-pill-btn ${studentMethod === 'quick' ? 'active' : ''}`}
                    onClick={() => setStudentMethod('quick')}
                    type="button"
                  >
                    Cấp nhanh
                  </button>
                  <button
                    className={`sub-pill-btn ${studentMethod === 'manual' ? 'active' : ''}`}
                    onClick={() => setStudentMethod('manual')}
                    type="button"
                  >
                    Điền thủ công
                  </button>
                  <button
                    className={`sub-pill-btn ${studentMethod === 'bulk' ? 'active' : ''}`}
                    onClick={() => setStudentMethod('bulk')}
                    type="button"
                  >
                    Dán từ Excel
                  </button>
                </div>
              )}

              {/* 1. STUDENT - QUICK GENERATE */}
              {accountType === 'student' && studentMethod === 'quick' && (
                <div className="create-form-content">
                  <div className="form-sub-heading">
                    <h4>Cấp nhanh tài khoản học viên</h4>
                    <p>Tạo hàng loạt tài khoản với thông tin ngẫu nhiên</p>
                  </div>

                  <form onSubmit={handleQuickGenerate} className="refined-form-fields">
                    {renderCenterScopeNotice()}

                    {/* QUANTITY */}
                    <div className="form-group-block">
                      <div className="group-label-row">
                        <label>Số lượng tài khoản</label>
                      </div>
                      <div className="chip-presets-row grid-6">
                        {QUANTITY_PRESETS.map((q) => (
                          <button
                            key={q}
                            type="button"
                            className={`modern-preset-chip ${Number(quickForm.quantity) === q ? 'active' : ''}`}
                            onClick={() => setQuickForm((prev) => ({ ...prev, quantity: q }))}
                          >
                            {q}
                          </button>
                        ))}
                        <button
                          type="button"
                          className={`modern-preset-chip ${!QUANTITY_PRESETS.includes(Number(quickForm.quantity)) ? 'active' : ''}`}
                          onClick={() => setQuickForm((prev) => ({ ...prev, quantity: '' }))}
                        >
                          Tùy chỉnh
                        </button>
                      </div>
                      <input
                        type="number"
                        required
                        min="1"
                        max="500"
                        value={quickForm.quantity}
                        onChange={(e) =>
                          setQuickForm((prev) => ({
                            ...prev,
                            quantity: e.target.value ? Number(e.target.value) : '',
                          }))
                        }
                        placeholder="Nhập số lượng tài khoản..."
                        className="modern-input-field"
                      />
                    </div>

                    {/* PREFIX */}
                    <div className="form-group-block">
                      <label>Tiền tố tài khoản</label>
                      <input
                        type="text"
                        value={quickForm.prefix}
                        onChange={(e) => setQuickForm((prev) => ({ ...prev, prefix: e.target.value }))}
                        placeholder="sv"
                        className="modern-input-field"
                      />
                      <span className="field-hint-text">Ví dụ: sv123456</span>
                    </div>

                    {/* Legacy classroom fields are retained in code/data but hidden from the account-only UI. */}
                    {ENABLE_CLASSROOM_FIELDS && <div className="form-group-block">
                      <div className="group-label-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <label style={{ margin: 0 }}>Gán vào Lớp học (Tùy chọn)</label>
                          <button
                            type="button"
                            onClick={handleOpenCreateClassModal}
                            style={{
                              background: '#eff6ff',
                              color: '#2563eb',
                              border: '1px solid #bfdbfe',
                              borderRadius: 4,
                              padding: '2px 8px',
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            + Tạo lớp mới
                          </button>
                        </div>
                        {selectedQuickClassroom?.ends_at && (
                          <span className="text-xs text-blue-600 font-medium">
                            Hạn lớp: {formatDate(selectedQuickClassroom.ends_at)}
                          </span>
                        )}
                      </div>
                      <select
                        value={quickForm.classroom_id}
                        onChange={(e) => handleQuickClassroomChange(e.target.value)}
                        className="modern-select-field"
                      >
                        <option value="">-- Không gán vào lớp học --</option>
                        {quickClassroomOptions.map((cls) => (
                          <option key={cls.id} value={cls.id}>
                            {cls.code} - {cls.name} {cls.ends_at ? `(Hạn: ${formatDate(cls.ends_at)})` : ''}
                          </option>
                        ))}
                      </select>
                      {selectedQuickClassroom && (
                        <div className="classroom-hint-card">
                          <div className="classroom-hint-icon">📚</div>
                          <div className="classroom-hint-body">
                            <strong>{selectedQuickClassroom.name}</strong> ({selectedQuickClassroom.code})
                            {selectedQuickClassroom.ends_at ? (
                              <p className="text-xs text-slate-600 mt-0.5">
                                ⚠️ <strong>Quy tắc khóa tài khoản:</strong> Khi lớp kết thúc, học viên có 24 giờ chờ gia hạn. Sau 1 ngày, tài khoản sẽ tự động <strong>bị khóa truy cập</strong>.
                              </p>
                            ) : (
                              <p className="text-xs text-slate-600 mt-0.5">Lớp học không giới hạn ngày kết thúc.</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>}

                    {/* EXPIRY */}
                    <div className="form-group-block">
                      <label>Hạn sử dụng</label>

                      <div className="chip-presets-row grid-4">
                        <button
                          type="button"
                          className={`modern-preset-chip ${quickForm.expires_date === getTodayPlusMonths(1) ? 'active' : ''}`}
                          onClick={() => setQuickForm((prev) => ({ ...prev, expires_date: getTodayPlusMonths(1) }))}
                        >
                          +1 tháng
                        </button>
                        <button
                          type="button"
                          className={`modern-preset-chip ${quickForm.expires_date === getTodayPlusMonths(3) ? 'active' : ''}`}
                          onClick={() => setQuickForm((prev) => ({ ...prev, expires_date: getTodayPlusMonths(3) }))}
                        >
                          +3 tháng
                        </button>
                        <button
                          type="button"
                          className={`modern-preset-chip ${quickForm.expires_date === getTodayPlusMonths(6) ? 'active' : ''}`}
                          onClick={() => setQuickForm((prev) => ({ ...prev, expires_date: getTodayPlusMonths(6) }))}
                        >
                          +6 tháng
                        </button>
                        <button
                          type="button"
                          className={`modern-preset-chip ${quickForm.expires_date === getTodayPlusMonths(12) ? 'active' : ''}`}
                          onClick={() => setQuickForm((prev) => ({ ...prev, expires_date: getTodayPlusMonths(12) }))}
                        >
                          +1 năm
                        </button>
                      </div>
                      <span className="field-hint-text">Hoặc chọn ngày cụ thể</span>
                      <input
                        type="date"
                        required
                        value={quickForm.expires_date}
                        onChange={(e) => setQuickForm((prev) => ({ ...prev, expires_date: e.target.value }))}
                        className="modern-input-field date-picker"
                      />
                    </div>

                    {/* QUOTA */}
                    <div className="form-group-block">
                      <label>Số lượt thi thử</label>
                      <div className="chip-presets-row grid-5">
                        {QUOTA_PRESETS.map((qp) => (
                          <button
                            key={qp}
                            type="button"
                            className={`modern-preset-chip ${
                              !quickForm.is_unlimited_quota && Number(quickForm.exam_quota) === qp ? 'active' : ''
                            }`}
                            onClick={() =>
                              setQuickForm((prev) => ({ ...prev, exam_quota: qp, is_unlimited_quota: false }))
                            }
                          >
                            {qp}
                          </button>
                        ))}
                        <button
                          type="button"
                          className={`modern-preset-chip ${quickForm.is_unlimited_quota ? 'active' : ''}`}
                          onClick={() => setQuickForm((prev) => ({ ...prev, exam_quota: 0, is_unlimited_quota: true }))}
                        >
                          Vô hạn
                        </button>
                      </div>
                      {!quickForm.is_unlimited_quota && (
                        <input
                          type="number"
                          min="1"
                          value={quickForm.exam_quota}
                          onChange={(e) =>
                            setQuickForm((prev) => ({
                              ...prev,
                              exam_quota: e.target.value ? Number(e.target.value) : '',
                            }))
                          }
                          placeholder="Tùy chỉnh số lượt"
                          className="modern-input-field"
                        />
                      )}
                    </div>

                    {/* CENTER AFFILIATION (ADMIN ONLY) */}
                    {isAdmin && centers.length > 0 && (
                      <div className="form-group-block">
                        <label>Gán Trung tâm trực thuộc</label>
                        <select
                          value={quickForm.center_id}
                          onChange={(e) => {
                            const centerId = e.target.value;
                            setQuickForm((prev) => {
                              const selectedCls = classrooms.find((c) => String(c.id) === String(prev.classroom_id));
                              return {
                                ...prev,
                                center_id: centerId,
                                classroom_id: selectedCls && classroomMatchesCenter(selectedCls, centerId) ? prev.classroom_id : '',
                              };
                            });
                          }}
                          className="modern-select-field"
                        >
                          <option value="">-- Không gán (Trực thuộc Hệ thống) --</option>
                          {centers.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.full_name || c.email}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* PRIMARY CTA BUTTON */}
                    <button disabled={saving} type="submit" className="btn-modern-cta-primary">
                      <PlusIcon className="w-5 h-5 inline mr-1.5" />
                      {saving ? 'Đang khởi tạo...' : 'Tạo tài khoản'}
                    </button>

                    {/* FOOTER NOTICE INFO BOX */}
                    <div className="create-panel-info-box">
                      <div className="info-box-icon">
                        <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <strong>Sau khi tạo tài khoản</strong>
                        <p>Hệ thống sẽ hiển thị danh sách tài khoản & mật khẩu vừa sinh, bạn có thể Copy All hoặc Xuất file CSV.</p>
                      </div>
                    </div>
                  </form>
                </div>
              )}

              {/* 2. STUDENT - MANUAL CREATE */}
              {accountType === 'student' && studentMethod === 'manual' && (
                <div className="create-form-content">
                  <div className="form-sub-heading">
                    <h4>Điền thủ công tài khoản học viên</h4>
                    <p>Tự điền tên đăng nhập, mật khẩu và họ tên học viên cụ thể</p>
                  </div>

                  <form onSubmit={handleCreateManualStudent} className="refined-form-fields">
                    {renderCenterScopeNotice()}

                    <div className="form-group-block">
                      <label>Họ và tên học viên <span className="text-red-500">*</span></label>
                      <input
                        required
                        value={manualForm.full_name}
                        onChange={(e) => setManualForm((prev) => ({ ...prev, full_name: e.target.value }))}
                        placeholder="Nguyễn Văn An"
                        className="modern-input-field"
                      />
                    </div>

                    <div className="form-group-block">
                      <label>Tên đăng nhập / Email <span className="text-red-500">*</span></label>
                      <input
                        required
                        value={manualForm.email}
                        onChange={(e) => setManualForm((prev) => ({ ...prev, email: e.target.value }))}
                        placeholder="hocvien_an"
                        className="modern-input-field"
                      />
                    </div>

                    <div className="form-group-block">
                      <label>Mật khẩu ban đầu <span className="text-red-500">*</span></label>
                      <input
                        required
                        minLength="6"
                        value={manualForm.password}
                        onChange={(e) => setManualForm((prev) => ({ ...prev, password: e.target.value }))}
                        placeholder="Tối thiểu 6 ký tự"
                        type="text"
                        className="modern-input-field"
                      />
                      <span className="field-hint-text">Tối thiểu 6 ký tự</span>
                    </div>


                    {/* CLASSROOM SELECTION (MANUAL) */}
                    {ENABLE_CLASSROOM_FIELDS && <div className="form-group-block">
                      <div className="group-label-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <label style={{ margin: 0 }}>Gán vào Lớp học (Tùy chọn)</label>
                          <button
                            type="button"
                            onClick={handleOpenCreateClassModal}
                            style={{
                              background: '#eff6ff',
                              color: '#2563eb',
                              border: '1px solid #bfdbfe',
                              borderRadius: 4,
                              padding: '2px 8px',
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            + Tạo lớp mới
                          </button>
                        </div>
                        {selectedManualClassroom?.ends_at && (
                          <span className="text-xs text-blue-600 font-medium">
                            Hạn lớp: {formatDate(selectedManualClassroom.ends_at)}
                          </span>
                        )}
                      </div>
                      <select
                        value={manualForm.classroom_id}
                        onChange={(e) => handleManualClassroomChange(e.target.value)}
                        className="modern-select-field"
                      >
                        <option value="">-- Không gán vào lớp học --</option>
                        {manualClassroomOptions.map((cls) => (
                          <option key={cls.id} value={cls.id}>
                            {cls.code} - {cls.name} {cls.ends_at ? `(Hạn: ${formatDate(cls.ends_at)})` : ''}
                          </option>
                        ))}
                      </select>
                      {selectedManualClassroom && (
                        <div className="classroom-hint-card">
                          <div className="classroom-hint-icon">📚</div>
                          <div className="classroom-hint-body">
                            <strong>{selectedManualClassroom.name}</strong> ({selectedManualClassroom.code})
                            {selectedManualClassroom.ends_at ? (
                              <p className="text-xs text-slate-600 mt-0.5">
                                ⚠️ <strong>Quy tắc khóa tài khoản:</strong> Khi lớp kết thúc, học viên có 24 giờ chờ gia hạn. Sau 1 ngày, tài khoản sẽ tự động <strong>bị khóa truy cập</strong>.
                              </p>
                            ) : (
                              <p className="text-xs text-slate-600 mt-0.5">Lớp học không giới hạn ngày kết thúc.</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>}

                    <div className="form-group-block">
                      <label>Hạn sử dụng cụ thể</label>

                      <div className="chip-presets-row grid-4">
                        <button
                          type="button"
                          className={`modern-preset-chip ${manualForm.expires_date === getTodayPlusMonths(1) ? 'active' : ''}`}
                          onClick={() => setManualForm((prev) => ({ ...prev, expires_date: getTodayPlusMonths(1) }))}
                        >
                          +1 tháng
                        </button>
                        <button
                          type="button"
                          className={`modern-preset-chip ${manualForm.expires_date === getTodayPlusMonths(3) ? 'active' : ''}`}
                          onClick={() => setManualForm((prev) => ({ ...prev, expires_date: getTodayPlusMonths(3) }))}
                        >
                          +3 tháng
                        </button>
                        <button
                          type="button"
                          className={`modern-preset-chip ${manualForm.expires_date === getTodayPlusMonths(6) ? 'active' : ''}`}
                          onClick={() => setManualForm((prev) => ({ ...prev, expires_date: getTodayPlusMonths(6) }))}
                        >
                          +6 tháng
                        </button>
                        <button
                          type="button"
                          className={`modern-preset-chip ${manualForm.expires_date === getTodayPlusMonths(12) ? 'active' : ''}`}
                          onClick={() => setManualForm((prev) => ({ ...prev, expires_date: getTodayPlusMonths(12) }))}
                        >
                          +1 năm
                        </button>
                      </div>
                      <input
                        type="date"
                        required
                        value={manualForm.expires_date}
                        onChange={(e) => setManualForm((prev) => ({ ...prev, expires_date: e.target.value }))}
                        className="modern-input-field date-picker"
                      />
                    </div>

                    <div className="form-group-block">
                      <label>Số lượt thi thử</label>
                      <div className="chip-presets-row grid-5">
                        {QUOTA_PRESETS.map((qp) => (
                          <button
                            key={qp}
                            type="button"
                            className={`modern-preset-chip ${
                              !manualForm.is_unlimited_quota && Number(manualForm.exam_quota) === qp ? 'active' : ''
                            }`}
                            onClick={() =>
                              setManualForm((prev) => ({ ...prev, exam_quota: qp, is_unlimited_quota: false }))
                            }
                          >
                            {qp}
                          </button>
                        ))}
                        <button
                          type="button"
                          className={`modern-preset-chip ${manualForm.is_unlimited_quota ? 'active' : ''}`}
                          onClick={() => setManualForm((prev) => ({ ...prev, exam_quota: 0, is_unlimited_quota: true }))}
                        >
                          Vô hạn
                        </button>
                      </div>
                      {!manualForm.is_unlimited_quota && (
                        <input
                          type="number"
                          min="1"
                          value={manualForm.exam_quota}
                          onChange={(e) =>
                            setManualForm((prev) => ({
                              ...prev,
                              exam_quota: e.target.value ? Number(e.target.value) : '',
                            }))
                          }
                          placeholder="Tùy chỉnh số lượt"
                          className="modern-input-field"
                        />
                      )}
                    </div>

                    {isAdmin && centers.length > 0 && (
                      <div className="form-group-block">
                        <label>Gán Trung tâm trực thuộc</label>
                        <select
                          value={manualForm.center_id}
                          onChange={(e) => {
                            const centerId = e.target.value;
                            setManualForm((prev) => {
                              const selectedCls = classrooms.find((c) => String(c.id) === String(prev.classroom_id));
                              return {
                                ...prev,
                                center_id: centerId,
                                classroom_id: selectedCls && classroomMatchesCenter(selectedCls, centerId) ? prev.classroom_id : '',
                              };
                            });
                          }}
                          className="modern-select-field"
                        >
                          <option value="">-- Không gán (Trực thuộc Hệ thống) --</option>
                          {centers.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.full_name || c.email}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <button disabled={saving} type="submit" className="btn-modern-cta-primary">
                      <PlusIcon className="w-5 h-5 inline mr-1.5" />
                      {saving ? 'Đang tạo...' : 'Tạo tài khoản'}
                    </button>
                  </form>
                </div>
              )}

              {/* 3. STUDENT - BULK CREATE */}
              {accountType === 'student' && studentMethod === 'bulk' && (
                <div className="create-form-content">
                  <div className="form-sub-heading">
                    <h4>Dán danh sách từ Excel</h4>
                    <p>Dán danh sách học viên định dạng: Họ tên, email, mật khẩu (mỗi dòng một người)</p>
                  </div>

                  <div className="refined-form-fields">
                    {renderCenterScopeNotice()}

                    <div className="form-group-block">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <label style={{ margin: 0 }}>Nội dung danh sách (Excel / Tab / Phẩy / Gạch dọc |)</label>
                        {bulkText.trim() && (
                          <button
                            type="button"
                            onClick={() => setBulkText('')}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#94a3b8',
                              fontSize: 12,
                              cursor: 'pointer',
                            }}
                          >
                            Xóa trắng
                          </button>
                        )}
                      </div>
                      <textarea
                        rows={4}
                        value={bulkText}
                        onChange={(e) => setBulkText(e.target.value)}
                        placeholder={'Nguyễn Văn A\thocvien01\t123456\nTrần Thị B\thocvien02\t123456\nhoặc: Họ tên | Username | Mật khẩu'}
                        className="modern-textarea-field"
                      />

                      {/* LIVE PRE-CHECK VALIDATION PREVIEW */}
                      {bulkText.trim() && (
                        <div className="bulk-precheck-box" style={{ marginTop: 8 }}>
                          <div className="bulk-precheck-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f1f5f9', borderRadius: '6px 6px 0 0', fontSize: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: '#334155' }}>
                              <SparklesIcon className="w-3.5 h-3.5 text-blue-600" />
                              <span>Kiểm tra trước ({parsedBulkPreview.length} dòng)</span>
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <span style={{ fontSize: 11, background: '#dcfce7', color: '#15803d', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>
                                ✓ {parsedBulkPreview.filter((i) => i.status === 'valid').length} hợp lệ
                              </span>
                              {parsedBulkPreview.some((i) => i.status !== 'valid') && (
                                <span style={{ fontSize: 11, background: '#fee2e2', color: '#b91c1c', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>
                                  ⚠️ {parsedBulkPreview.filter((i) => i.status !== 'valid').length} lỗi/trùng
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="bulk-precheck-list" style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 6px 6px', background: '#fff' }}>
                            {parsedBulkPreview.map((item) => (
                              <div
                                key={item.index}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '5px 10px',
                                  fontSize: 12,
                                  borderBottom: '1px dashed #f1f5f9',
                                  background: item.status === 'valid' ? '#fff' : '#fff1f2',
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, overflow: 'hidden' }}>
                                  <span style={{ color: '#94a3b8', fontSize: 10, width: 16 }}>{item.index}.</span>
                                  <strong style={{ color: '#0f172a', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: 120 }}>
                                    {item.name}
                                  </strong>
                                  <span style={{ color: '#475569', fontSize: 11 }}>({item.email})</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                  <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#047857', background: '#ecfdf5', padding: '1px 4px', borderRadius: 3 }}>
                                    {item.password}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      padding: '1px 5px',
                                      borderRadius: 4,
                                      color: item.status === 'valid' ? '#15803d' : '#b91c1c',
                                      background: item.status === 'valid' ? '#dcfce7' : '#fee2e2',
                                    }}
                                  >
                                    {item.status === 'valid' ? 'Hợp lệ' : item.errorMsg}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* CLASSROOM SELECTION (BULK) */}
                    {ENABLE_CLASSROOM_FIELDS && <div className="form-group-block">
                      <div className="group-label-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <label style={{ margin: 0 }}>Gán vào Lớp học (Tùy chọn)</label>
                          <button
                            type="button"
                            onClick={handleOpenCreateClassModal}
                            style={{
                              background: '#eff6ff',
                              color: '#2563eb',
                              border: '1px solid #bfdbfe',
                              borderRadius: 4,
                              padding: '2px 8px',
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            + Tạo lớp mới
                          </button>
                        </div>
                        {selectedBulkClassroom?.ends_at && (
                          <span className="text-xs text-blue-600 font-medium">
                            Hạn lớp: {formatDate(selectedBulkClassroom.ends_at)}
                          </span>
                        )}
                      </div>
                      <select
                        value={bulkClassroomId}
                        onChange={(e) => handleBulkClassroomChange(e.target.value)}
                        className="modern-select-field"
                      >
                        <option value="">-- Không gán vào lớp học --</option>
                        {bulkClassroomOptions.map((cls) => (
                          <option key={cls.id} value={cls.id}>
                            {cls.code} - {cls.name} {cls.ends_at ? `(Hạn: ${formatDate(cls.ends_at)})` : ''}
                          </option>
                        ))}
                      </select>
                      {selectedBulkClassroom && (
                        <div className="classroom-hint-card">
                          <div className="classroom-hint-icon">📚</div>
                          <div className="classroom-hint-body">
                            <strong>{selectedBulkClassroom.name}</strong> ({selectedBulkClassroom.code})
                            {selectedBulkClassroom.ends_at ? (
                              <p className="text-xs text-slate-600 mt-0.5">
                                ⚠️ <strong>Quy tắc khóa tài khoản:</strong> Khi lớp kết thúc, học viên có 24 giờ chờ gia hạn. Sau 1 ngày, tài khoản sẽ tự động <strong>bị khóa truy cập</strong>.
                              </p>
                            ) : (
                              <p className="text-xs text-slate-600 mt-0.5">Lớp học không giới hạn ngày kết thúc.</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>}

                    <div className="form-group-block">
                      <label>Hạn sử dụng chung</label>

                      <div className="chip-presets-row grid-4">
                        <button
                          type="button"
                          className={`modern-preset-chip ${bulkExpiresDate === getTodayPlusMonths(1) ? 'active' : ''}`}
                          onClick={() => setBulkExpiresDate(getTodayPlusMonths(1))}
                        >
                          +1 tháng
                        </button>
                        <button
                          type="button"
                          className={`modern-preset-chip ${bulkExpiresDate === getTodayPlusMonths(3) ? 'active' : ''}`}
                          onClick={() => setBulkExpiresDate(getTodayPlusMonths(3))}
                        >
                          +3 tháng
                        </button>
                        <button
                          type="button"
                          className={`modern-preset-chip ${bulkExpiresDate === getTodayPlusMonths(6) ? 'active' : ''}`}
                          onClick={() => setBulkExpiresDate(getTodayPlusMonths(6))}
                        >
                          +6 tháng
                        </button>
                        <button
                          type="button"
                          className={`modern-preset-chip ${bulkExpiresDate === getTodayPlusMonths(12) ? 'active' : ''}`}
                          onClick={() => setBulkExpiresDate(getTodayPlusMonths(12))}
                        >
                          +1 năm
                        </button>
                      </div>
                      <input
                        type="date"
                        required
                        value={bulkExpiresDate}
                        onChange={(e) => setBulkExpiresDate(e.target.value)}
                        className="modern-input-field date-picker"
                      />
                    </div>

                    <div className="form-group-block">
                      <label>Số lượt thi chung</label>
                      <div className="chip-presets-row grid-5">
                        {QUOTA_PRESETS.map((qp) => (
                          <button
                            key={qp}
                            type="button"
                            className={`modern-preset-chip ${
                              !bulkUnlimitedQuota && Number(bulkExamQuota) === qp ? 'active' : ''
                            }`}
                            onClick={() => {
                              setBulkExamQuota(qp);
                              setBulkUnlimitedQuota(false);
                            }}
                          >
                            {qp}
                          </button>
                        ))}
                        <button
                          type="button"
                          className={`modern-preset-chip ${bulkUnlimitedQuota ? 'active' : ''}`}
                          onClick={() => {
                            setBulkExamQuota(0);
                            setBulkUnlimitedQuota(true);
                          }}
                        >
                          Vô hạn
                        </button>
                      </div>
                    </div>

                    {isAdmin && centers.length > 0 && (
                      <div className="form-group-block">
                        <label>Gán Trung tâm trực thuộc</label>
                        <select
                          value={bulkCenterId}
                          onChange={(e) => {
                            const centerId = e.target.value;
                            const selectedCls = classrooms.find((c) => String(c.id) === String(bulkClassroomId));
                            setBulkCenterId(centerId);
                            if (selectedCls && !classroomMatchesCenter(selectedCls, centerId)) {
                              setBulkClassroomId('');
                            }
                          }}
                          className="modern-select-field"
                        >
                          <option value="">-- Không gán (Trực thuộc Hệ thống) --</option>
                          {centers.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.full_name || c.email}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <button
                      disabled={saving || !bulkText.trim() || !parsedBulkPreview.some((i) => i.status === 'valid')}
                      onClick={handleBulkCreate}
                      type="button"
                      className="btn-modern-cta-primary"
                    >
                      <PlusIcon className="w-5 h-5 inline mr-1.5" />
                      {saving
                        ? 'Đang xử lý...'
                        : `Tạo ${parsedBulkPreview.filter((i) => i.status === 'valid').length || 0} tài khoản hợp lệ`}
                    </button>
                  </div>
                </div>
              )}

              {/* 4. CENTER CREATE (ADMIN ONLY) */}
              {accountType === 'center' && isAdmin && (
                <div className="create-form-content">
                  <div className="form-sub-heading">
                    <h4>Cấp tài khoản Trung tâm đối tác</h4>
                    <p>Trung tâm có quyền quản lý và cấp tài khoản học viên trực thuộc</p>
                  </div>

                  <form onSubmit={handleCreateCenter} className="refined-form-fields">
                    <div className="form-group-block">
                      <label>Tên Trung tâm / Chi nhánh <span className="text-red-500">*</span></label>
                      <input
                        required
                        value={centerForm.full_name}
                        onChange={(e) => setCenterForm((prev) => ({ ...prev, full_name: e.target.value }))}
                        placeholder="Tin học Sao Việt Chi nhánh Q1"
                        className="modern-input-field"
                      />
                    </div>

                    <div className="form-group-block">
                      <label>Email / Tên đăng nhập Trung tâm <span className="text-red-500">*</span></label>
                      <input
                        required
                        value={centerForm.email}
                        onChange={(e) => setCenterForm((prev) => ({ ...prev, email: e.target.value }))}
                        placeholder="trungtam_q1"
                        className="modern-input-field"
                      />
                    </div>

                    <div className="form-group-block">
                      <label>Mật khẩu đăng nhập <span className="text-red-500">*</span></label>
                      <input
                        required
                        minLength="6"
                        value={centerForm.password}
                        onChange={(e) => setCenterForm((prev) => ({ ...prev, password: e.target.value }))}
                        placeholder="Tối thiểu 6 ký tự"
                        type="text"
                        className="modern-input-field"
                      />
                    </div>

                    <div className="form-group-block">
                      <label>Hạn sử dụng Trung tâm</label>
                      <div className="chip-presets-row grid-3">
                        <button
                          type="button"
                          className={`modern-preset-chip ${centerForm.expires_date === getTodayPlusMonths(6) ? 'active' : ''}`}
                          onClick={() => setCenterForm((prev) => ({ ...prev, expires_date: getTodayPlusMonths(6) }))}
                        >
                          +6 tháng
                        </button>
                        <button
                          type="button"
                          className={`modern-preset-chip ${centerForm.expires_date === getTodayPlusMonths(12) ? 'active' : ''}`}
                          onClick={() => setCenterForm((prev) => ({ ...prev, expires_date: getTodayPlusMonths(12) }))}
                        >
                          +1 năm
                        </button>
                        <button
                          type="button"
                          className={`modern-preset-chip ${centerForm.expires_date === getTodayPlusMonths(24) ? 'active' : ''}`}
                          onClick={() => setCenterForm((prev) => ({ ...prev, expires_date: getTodayPlusMonths(24) }))}
                        >
                          +2 năm
                        </button>
                      </div>
                      <input
                        type="date"
                        required
                        value={centerForm.expires_date}
                        onChange={(e) => setCenterForm((prev) => ({ ...prev, expires_date: e.target.value }))}
                        className="modern-input-field date-picker"
                      />
                    </div>

                    <button disabled={saving} type="submit" className="btn-modern-cta-primary">
                      <PlusIcon className="w-5 h-5 inline mr-1.5" />
                      {saving ? 'Đang tạo...' : 'Tạo tài khoản Trung tâm'}
                    </button>
                  </form>
                </div>
              )}

              {/* 5. ADMIN CREATE (ADMIN ONLY) */}
              {accountType === 'admin' && isAdmin && (
                <div className="create-form-content">
                  <div className="form-sub-heading">
                    <h4>🛡️ Cấp tài khoản Quản trị viên</h4>
                    <p>Quản trị viên có toàn quyền quản lý hệ thống, trung tâm và học viên</p>
                  </div>

                  <form onSubmit={handleCreateAdmin} className="refined-form-fields">
                    <div
                      style={{
                        background: '#eef2ff',
                        border: '1px solid #c7d2fe',
                        borderRadius: '8px',
                        padding: '10px 12px',
                        fontSize: '12px',
                        color: '#3730a3',
                        lineHeight: 1.45,
                      }}
                    >
                      🔒 <strong>Bảo mật phân quyền:</strong> Chỉ tài khoản Quản trị mới nhìn thấy và quản lý được danh sách tài khoản Quản trị viên. Các Trung tâm đối tác hoàn toàn không nhìn thấy tài khoản này.
                    </div>

                    <div className="form-group-block">
                      <label>Họ và tên Quản trị viên <span className="text-red-500">*</span></label>
                      <input
                        required
                        value={adminForm.full_name}
                        onChange={(e) => setAdminForm((prev) => ({ ...prev, full_name: e.target.value }))}
                        placeholder="Nguyễn Quản Trị"
                        className="modern-input-field"
                      />
                    </div>

                    <div className="form-group-block">
                      <label>Tên đăng nhập / Email Quản trị <span className="text-red-500">*</span></label>
                      <input
                        required
                        value={adminForm.email}
                        onChange={(e) => setAdminForm((prev) => ({ ...prev, email: e.target.value }))}
                        placeholder="admin_saoviet02"
                        className="modern-input-field"
                      />
                    </div>

                    <div className="form-group-block">
                      <label>Mật khẩu đăng nhập <span className="text-red-500">*</span></label>
                      <input
                        required
                        minLength="6"
                        value={adminForm.password}
                        onChange={(e) => setAdminForm((prev) => ({ ...prev, password: e.target.value }))}
                        placeholder="Tối thiểu 6 ký tự"
                        type="text"
                        className="modern-input-field"
                      />
                    </div>

                    <div className="form-group-block">
                      <label>Thời hạn tài khoản Quản trị</label>
                      <div className="chip-presets-row grid-3">
                        <button
                          type="button"
                          className={`modern-preset-chip ${!adminForm.expires_date ? 'active' : ''}`}
                          onClick={() => setAdminForm((prev) => ({ ...prev, expires_date: '' }))}
                        >
                          ♾️ Vô hạn
                        </button>
                        <button
                          type="button"
                          className={`modern-preset-chip ${adminForm.expires_date === getTodayPlusMonths(12) ? 'active' : ''}`}
                          onClick={() => setAdminForm((prev) => ({ ...prev, expires_date: getTodayPlusMonths(12) }))}
                        >
                          +1 năm
                        </button>
                        <button
                          type="button"
                          className={`modern-preset-chip ${adminForm.expires_date === getTodayPlusMonths(24) ? 'active' : ''}`}
                          onClick={() => setAdminForm((prev) => ({ ...prev, expires_date: getTodayPlusMonths(24) }))}
                        >
                          +2 năm
                        </button>
                      </div>
                      {adminForm.expires_date ? (
                        <input
                          type="date"
                          value={adminForm.expires_date}
                          onChange={(e) => setAdminForm((prev) => ({ ...prev, expires_date: e.target.value }))}
                          className="modern-input-field date-picker"
                        />
                      ) : (
                        <span className="field-hint-text">Mặc định: Vô hạn (Không giới hạn ngày hết hạn)</span>
                      )}
                    </div>

                    <button disabled={saving} type="submit" className="btn-modern-cta-primary">
                      <PlusIcon className="w-5 h-5 inline mr-1.5" />
                      {saving ? 'Đang tạo...' : 'Tạo tài khoản Quản trị'}
                    </button>
                  </form>
                </div>
              )}
            </aside>
          </div>
        </main>
      </div>

      {/* MODAL: CỘNG THÊM LƯỢT THI (QUOTA MODAL) */}
      {quotaModalAccount && (
        <div className="account-modal-backdrop" role="presentation">
          <section className="account-modal" role="dialog" aria-modal="true">
            <div className="modal-result-header">
              <div>
                <h3>Cộng lượt thi thử</h3>
                <p>
                  Học viên: <strong>{quotaModalAccount.full_name || quotaModalAccount.email}</strong>
                </p>
              </div>
              <button
                className="modal-close-icon"
                onClick={() => setQuotaModalAccount(null)}
                aria-label="Đóng"
                type="button"
              >
                <XMarkIcon />
              </button>
            </div>

            <div className="quota-current-summary">
              <div>
                <span>Lượt đã thi:</span>
                <strong>{quotaModalAccount.exam_used || 0} lượt</strong>
              </div>
              <div>
                <span>Đã luyện tập:</span>
                <strong style={{ color: '#7c3aed' }}>{quotaModalAccount.practice_used || 0} lượt</strong>
              </div>
              <div>
                <span>Hạn mức thi:</span>
                <strong>
                  {quotaModalAccount.exam_quota ? `${quotaModalAccount.exam_quota} lượt` : 'Không giới hạn'}
                </strong>
              </div>
            </div>

            <form onSubmit={handleSaveQuota}>
              <label>
                Cộng thêm lượt thi nhanh:
                <div className="quick-quota-buttons">
                  {[5, 10, 20, 50].map((addNum) => (
                    <button
                      key={addNum}
                      type="button"
                      className={quotaAddValue === addNum && quotaSetValue === '' ? 'active' : ''}
                      onClick={() => {
                        setQuotaAddValue(addNum);
                        setQuotaSetValue('');
                      }}
                    >
                      +{addNum} lượt
                    </button>
                  ))}
                </div>
              </label>

              <label>
                Hoặc nhập số lượt cộng thêm:
                <input
                  type="number"
                  min="1"
                  value={quotaSetValue === '' ? quotaAddValue : ''}
                  onChange={(e) => {
                    setQuotaAddValue(Number(e.target.value));
                    setQuotaSetValue('');
                  }}
                  placeholder="Ví dụ: 15"
                />
              </label>

              <label>
                Hoặc đặt lại tổng số lượt mới (Ghi đè):
                <input
                  type="number"
                  min="0"
                  value={quotaSetValue}
                  onChange={(e) => setQuotaSetValue(e.target.value)}
                  placeholder="Để trống nếu chỉ cộng thêm lượt"
                />
                <span className="account-field-hint">Nhập 0 để đặt thành Không giới hạn lượt thi.</span>
              </label>

              <div className="modal-actions-footer">
                <button onClick={() => setQuotaModalAccount(null)} type="button">
                  Hủy
                </button>
                <button disabled={saving} type="submit" className="btn-modal-submit">
                  Lưu số lượt thi
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {/* MODAL: GIA HẠN NGÀY CỤ THỂ (EXTEND MODAL) */}
      {extendModalAccount && (
        <div className="account-modal-backdrop" role="presentation">
          <section className="account-modal" role="dialog" aria-modal="true">
            <div className="modal-result-header">
              <div>
                <h3>Gia hạn ngày sử dụng</h3>
                <p>
                  Tài khoản: <strong>{extendModalAccount.full_name || extendModalAccount.email}</strong>
                </p>
              </div>
              <button
                className="modal-close-icon"
                onClick={() => setExtendModalAccount(null)}
                aria-label="Đóng"
                type="button"
              >
                <XMarkIcon />
              </button>
            </div>

            <div className="extend-current-summary">
              <span>Hạn dùng hiện tại:</span>
              <strong>{formatDate(extendModalAccount.expires_at)}</strong>
            </div>

            <form onSubmit={handleSaveExtend}>
              <label>
                Chọn ngày hết hạn mới:
                <div className="quick-date-buttons">
                  <button type="button" onClick={() => setExtendDateValue(getTodayPlusMonths(1))}>
                    1 tháng nữa
                  </button>
                  <button type="button" onClick={() => setExtendDateValue(getTodayPlusMonths(3))}>
                    3 tháng nữa
                  </button>
                  <button type="button" onClick={() => setExtendDateValue(getTodayPlusMonths(6))}>
                    6 tháng nữa
                  </button>
                  <button type="button" onClick={() => setExtendDateValue(getTodayPlusMonths(12))}>
                    1 năm nữa
                  </button>
                  <button
                    type="button"
                    onClick={() => setExtendDateValue('unlimited')}
                    style={{
                      background: extendDateValue === 'unlimited' ? '#059669' : '#f0fdf4',
                      color: extendDateValue === 'unlimited' ? '#ffffff' : '#047857',
                      borderColor: '#10b981',
                      fontWeight: 600,
                    }}
                  >
                    ♾️ Vô hạn
                  </button>
                </div>
                {extendDateValue === 'unlimited' ? (
                  <div
                    style={{
                      margin: '12px 0 8px',
                      padding: '12px 14px',
                      background: '#ecfdf5',
                      border: '1px solid #a7f3d0',
                      color: '#065f46',
                      borderRadius: '8px',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>♾️</span>
                    <span>Tài khoản sẽ được đặt thời hạn <strong>Vô hạn (Không bao giờ hết hạn)</strong>.</span>
                  </div>
                ) : (
                  <input
                    type="date"
                    required
                    value={extendDateValue}
                    onChange={(e) => setExtendDateValue(e.target.value)}
                    className="date-picker-input"
                  />
                )}
              </label>

              <div className="modal-actions-footer">
                <button onClick={() => setExtendModalAccount(null)} type="button">
                  Hủy
                </button>
                <button disabled={saving} type="submit" className="btn-modal-submit">
                  Lưu ngày gia hạn
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {/* MODAL: KẾT QUẢ CẤP TÀI KHOẢN NGẪU NHIÊN */}
      {generatedResults && (
        <div className="account-modal-backdrop" role="presentation">
          <section className="account-modal generated-results-modal" role="dialog" aria-modal="true">
            <div className="modal-result-header">
              <div>
                <div className="badge-success-count">
                  <CheckCircleIcon className="w-5 h-5 inline mr-1" /> Đã cấp {generatedResults.count} tài khoản
                </div>
                <h3>Danh sách tài khoản vừa tạo</h3>
                <p>Sao chép hoặc tải file CSV để gửi cho học viên.</p>
              </div>
              <button
                className="modal-close-icon"
                onClick={() => setGeneratedResults(null)}
                aria-label="Đóng"
                type="button"
              >
                <XMarkIcon />
              </button>
            </div>

            <div className="modal-result-actions">
              <button onClick={handleCopyAccounts} className="btn-copy-all" type="button">
                <DocumentDuplicateIcon className="w-4 h-4 inline mr-1" />
                {copied ? 'Đã sao chép vào bộ nhớ tạm! ✓' : 'Sao chép toàn bộ danh sách'}
              </button>
              <button onClick={() => setExportModalAccounts(generatedResults.accounts)} className="btn-download-csv" type="button">
                <DocumentTextIcon className="w-4 h-4 inline mr-1" />
                Xuất file TXT / Định dạng
              </button>
            </div>

            <div className="modal-result-table-wrap">
              <table className="modal-result-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Tài khoản</th>
                    <th>Mật khẩu</th>
                    <th>Lượt thi</th>
                    <th>Hạn dùng</th>
                  </tr>
                </thead>
                <tbody>
                  {generatedResults.accounts.map((item, idx) => (
                    <tr key={item.id}>
                      <td>{idx + 1}</td>
                      <td>
                        <strong className="code-text">{item.username}</strong>
                      </td>
                      <td>
                        <code className="password-badge">{item.password}</code>
                      </td>
                      <td>
                        <span className="badge-quota-item">
                          {item.exam_quota ? `${item.exam_quota} lượt` : 'Vô hạn'}
                        </span>
                      </td>
                      <td>{formatDate(item.expires_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="modal-result-footer">
              <p className="notice-text">
                📌 Khi đăng nhập lần đầu, học viên sẽ được yêu cầu nhập Họ tên và SĐT cá nhân.
              </p>
              <button onClick={() => setGeneratedResults(null)} className="btn-close-modal" type="button">
                Hoàn tất & Đóng
              </button>
            </div>
          </section>
        </div>
      )}

      {/* MODAL: RESET PASSWORD */}
      {passwordModalAccount && (
        <div
          className="account-modal-backdrop"
          onMouseDown={(event) => event.target === event.currentTarget && setPasswordModalAccount(null)}
          role="presentation"
        >
          <section className="account-modal" role="dialog" aria-modal="true">
            <h3>Đặt lại mật khẩu</h3>
            <p>{passwordModalAccount.full_name || passwordModalAccount.email}</p>
            <form onSubmit={handleResetPassword}>
              <input
                autoFocus
                minLength="6"
                required
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                placeholder="Mật khẩu mới (tối thiểu 6 ký tự)"
              />
              <div className="modal-actions-footer">
                <button onClick={() => setPasswordModalAccount(null)} type="button">
                  Hủy
                </button>
                <button disabled={saving} type="submit" className="btn-modal-submit">
                  Lưu mật khẩu
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {/* MODAL: CREATE CLASSROOM */}
      {showCreateClassModal && (
        <div
          className="account-modal-backdrop"
          onMouseDown={(event) => event.target === event.currentTarget && !creatingClass && setShowCreateClassModal(false)}
          role="presentation"
        >
          <section className="account-modal" style={{ maxWidth: 520 }} role="dialog" aria-modal="true">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: '#0f172a' }}>Tạo lớp học mới</h3>
              <button
                type="button"
                onClick={() => setShowCreateClassModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <XMarkIcon className="w-5 h-5 text-slate-400 hover:text-slate-600" />
              </button>
            </div>
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>
              Lớp học sẽ tự động được gán với trung tâm của bạn. Học viên sau này sẽ được gán thời hạn sử dụng khớp với ngày kết thúc của lớp.
            </p>
            <form onSubmit={handleCreateNewClassroom}>
              <div className="form-group-block" style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <label style={{ margin: 0 }}>Mã lớp học <span className="text-red-500">*</span></label>
                  <span className="code-sub-badge">Mã ngẫu nhiên</span>
                </div>
                <div className="class-code-input-row">
                  <input
                    required
                    value={newClassForm.code}
                    onChange={(e) => setNewClassForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    placeholder="Ví dụ: DN-K8492"
                    className="class-code-input"
                  />
                  <button
                    type="button"
                    className="btn-randomize-code"
                    onClick={handleRandomizeNewClassCode}
                    title="Bấm để đổi mã ngẫu nhiên khác"
                  >
                    <ArrowPathIcon className="w-4 h-4" /> 🎲 Tạo mã mới
                  </button>
                </div>
                <small style={{ fontSize: 11, color: '#64748b', marginTop: 4, display: 'block' }}>
                  Mã lớp được tạo ngẫu nhiên theo trung tâm của bạn. Bạn có thể nhấn đổi mã khác hoặc tự nhập mã riêng.
                </small>
              </div>

              <div className="form-group-block" style={{ marginBottom: 14 }}>
                <label>Tên lớp học <span className="text-red-500">*</span></label>
                <input
                  required
                  value={newClassForm.name}
                  onChange={(e) => setNewClassForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Ví dụ: Lớp Ôn Thi GenAI K01 - Đồng Nai"
                  className="modern-input-field"
                />
              </div>

              <div className="form-group-block" style={{ marginBottom: 14 }}>
                <label>Mô tả lớp học (Tùy chọn)</label>
                <textarea
                  rows={2}
                  value={newClassForm.description}
                  onChange={(e) => setNewClassForm((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Ghi chú về giảng viên, ca học hoặc phòng máy..."
                  className="modern-textarea-field"
                />
              </div>

              <div className="form-group-block" style={{ marginBottom: 14 }}>
                <label style={{ marginBottom: 6 }}>Thời hạn khóa học</label>
                <div className="class-duration-chips">
                  {[
                    [1, '+1 tháng'],
                    [2, '+2 tháng'],
                    [3, '+3 tháng'],
                    [6, '+6 tháng'],
                    [0, 'Không giới hạn'],
                  ].map(([m, label]) => (
                    <button
                      key={m}
                      type="button"
                      className={`class-duration-chip ${newClassDurationChoice === m ? 'active' : ''}`}
                      onClick={() => handleApplyNewClassDuration(m)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="class-date-grid">
                  <div className="class-date-col">
                    <label>Ngày bắt đầu</label>
                    <input
                      type="datetime-local"
                      value={newClassForm.starts_at}
                      onChange={(e) => setNewClassForm((prev) => ({ ...prev, starts_at: e.target.value }))}
                      className="class-date-input"
                    />
                  </div>
                  <div className="class-date-col">
                    <label>Ngày kết thúc</label>
                    <input
                      type="datetime-local"
                      value={newClassForm.ends_at}
                      onChange={(e) => setNewClassForm((prev) => ({ ...prev, ends_at: e.target.value }))}
                      className="class-date-input"
                    />
                  </div>
                </div>
                <small style={{ fontSize: 11, color: '#64748b', marginTop: 4, display: 'block' }}>
                  Học viên được gán vào lớp sẽ tự động được gia hạn thi khớp với ngày kết thúc của lớp.
                </small>
              </div>
              <div className="modal-actions-footer">
                <button
                  type="button"
                  disabled={creatingClass}
                  onClick={() => setShowCreateClassModal(false)}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={creatingClass}
                  className="btn-modal-submit"
                >
                  {creatingClass ? 'Đang tạo...' : 'Tạo lớp học'}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {/* MODAL: XUẤT & SAO CHÉP DANH SÁCH TÀI KHOẢN (TXT / TSV / COMPACT) */}
      {exportModalAccounts && (
        <div
          className="export-modal-backdrop"
          onClick={(e) => e.target === e.currentTarget && setExportModalAccounts(null)}
        >
          <div className="export-modal-card" role="dialog" aria-modal="true">
            <div className="export-modal-header">
              <div className="export-modal-title-group">
                <div className="export-modal-icon-badge">
                  <DocumentTextIcon className="w-6 h-6" />
                </div>
                <div>
                  <h3>Xuất danh sách tài khoản & mật khẩu</h3>
                  <p>
                    Đang chọn <strong>{exportModalAccounts.length}</strong> tài khoản • Dễ dàng sao chép dán vào Zalo/Word hoặc tải file .TXT
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setExportModalAccounts(null)}
                className="search-clear-btn"
                title="Đóng cửa sổ"
                style={{ width: 32, height: 32 }}
              >
                <XMarkIcon className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="export-modal-tabs">
              <button
                type="button"
                className={`export-tab-btn ${exportFormatTab === 'detail' ? 'active' : ''}`}
                onClick={() => setExportFormatTab('detail')}
              >
                📋 Chi tiết gửi học viên (TXT)
              </button>
              <button
                type="button"
                className={`export-tab-btn ${exportFormatTab === 'compact' ? 'active' : ''}`}
                onClick={() => setExportFormatTab('compact')}
              >
                📑 Danh sách 1 dòng (Gọn)
              </button>
              <button
                type="button"
                className={`export-tab-btn ${exportFormatTab === 'tsv' ? 'active' : ''}`}
                onClick={() => setExportFormatTab('tsv')}
              >
                📊 Bảng dán vào Excel / Google Sheets
              </button>
            </div>

            <div className="export-modal-body">
              <div className="export-textarea-wrap">
                <textarea
                  className="export-textarea"
                  readOnly
                  value={exportText}
                  onFocus={(e) => e.target.select()}
                  title="Bấm vào để chọn tất cả"
                />
              </div>
              <div className="export-modal-tip">
                💡 <strong>Mẹo hữu ích:</strong> Bấm nút <strong>"Sao chép toàn bộ"</strong> màu xanh bên dưới để dán ngay vào tin nhắn Zalo gửi học viên, hoặc dán thẳng vào Notepad / Word / Excel mà không lo lỗi tải file!
              </div>
            </div>

            <div className="export-modal-footer">
              <button
                type="button"
                onClick={() => setExportModalAccounts(null)}
                className="btn-export-download-file"
              >
                Đóng
              </button>

              <div className="export-footer-actions">
                <button
                  type="button"
                  onClick={handleDownloadTxtFile}
                  className="btn-export-download-file"
                  title="Tải về file văn bản .TXT lưu trong máy tính"
                >
                  <ArrowDownTrayIcon className="w-4 h-4 text-blue-600" />
                  <span>Tải file .TXT</span>
                </button>

                <button
                  type="button"
                  onClick={handleCopyExportText}
                  className={`btn-export-copy-main ${exportCopied ? 'copied' : ''}`}
                >
                  {exportCopied ? (
                    <>
                      <CheckCircleIcon className="w-5 h-5 text-white" />
                      <span>Đã sao chép vào bộ nhớ tạm!</span>
                    </>
                  ) : (
                    <>
                      <DocumentDuplicateIcon className="w-5 h-5" />
                      <span>Sao chép toàn bộ danh sách</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: XÓA HÀNG LOẠT (BULK DELETE MODAL) */}
      {showBulkDeleteModal && (
        <div className="account-modal-backdrop" role="presentation" onClick={() => !bulkDeleting && setShowBulkDeleteModal(false)}>
          <section className="account-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }}>
            <div className="modal-result-header" style={{ borderBottom: '1px solid #fee2e2', paddingBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <TrashIcon style={{ width: 22, height: 22 }} />
                </div>
                <div>
                  <h3 style={{ color: '#991b1b', margin: 0, fontSize: 18 }}>Xác nhận xóa hàng loạt</h3>
                  <p style={{ margin: '3px 0 0', fontSize: 13, color: '#64748b' }}>
                    {selectedIds.length > 0 ? 'Xóa theo danh sách tài khoản đã chọn' : 'Xóa theo danh sách bộ lọc hiện tại'}
                  </p>
                </div>
              </div>
              <button
                className="modal-close-icon"
                onClick={() => !bulkDeleting && setShowBulkDeleteModal(false)}
                aria-label="Đóng"
                type="button"
                disabled={bulkDeleting}
              >
                <XMarkIcon />
              </button>
            </div>

            <div style={{ padding: '20px 0 10px' }}>
              <div
                style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 10,
                  padding: '14px 16px',
                  color: '#991b1b',
                  fontSize: 13.5,
                  lineHeight: 1.55,
                  marginBottom: 16,
                }}
              >
                <strong>⚠️ Cảnh báo xóa vĩnh viễn:</strong> Bạn đang yêu cầu xóa vĩnh viễn{' '}
                <strong style={{ textDecoration: 'underline' }}>{accountsToDelete.length} tài khoản</strong>. Thao tác này sẽ xoá hoàn toàn thông tin đăng nhập và toàn bộ bài thi thử/kết quả ôn luyện liên quan. Hành động này <strong>KHÔNG THỂ hoàn tác</strong>.
              </div>

              <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', background: '#f8fafc' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
                  Danh sách tài khoản chuẩn bị xóa ({accountsToDelete.length}):
                </div>
                {accountsToDelete.slice(0, 10).map((acc, idx) => (
                  <div key={acc.id} style={{ fontSize: 13, color: '#1e293b', padding: '3px 0', borderBottom: '1px dashed #e2e8f0' }}>
                    {idx + 1}. <strong>{acc.email}</strong> ({acc.full_name || 'Học viên'}) - {acc.role === 'center' ? 'Trung tâm' : 'Học viên'}
                  </div>
                ))}
                {accountsToDelete.length > 10 && (
                  <div style={{ fontSize: 12, fontStyle: 'italic', color: '#64748b', marginTop: 6 }}>
                    ... và {accountsToDelete.length - 10} tài khoản khác.
                  </div>
                )}
              </div>

              {/* BẮT BUỘC GÕ TỪ KHÓA ĐỂ MỞ KHÓA NÚT XÓA */}
              <div style={{ marginTop: 18, background: '#fff', border: '1.5px solid #fecaca', borderRadius: 8, padding: '12px 14px' }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#991b1b', marginBottom: 6 }}>
                  Để tránh xóa nhầm dữ liệu, vui lòng gõ chính xác chữ <span style={{ background: '#fee2e2', padding: '2px 8px', borderRadius: 4, fontFamily: 'monospace', fontSize: 14 }}>OK</span> vào ô dưới đây:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && isBulkDeleteConfirmed && !bulkDeleting) {
                      e.preventDefault();
                      handleConfirmBulkDelete();
                    }
                  }}
                  placeholder="Gõ chữ OK..."
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    borderRadius: 6,
                    border: '1.5px solid #f87171',
                    fontSize: 14,
                    fontWeight: 600,
                    outline: 'none',
                    boxSizing: 'border-box',
                    background: '#fff',
                  }}
                  autoFocus
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button
                type="button"
                onClick={() => {
                  setShowBulkDeleteModal(false);
                  setDeleteConfirmText('');
                }}
                disabled={bulkDeleting}
                style={{
                  padding: '10px 18px',
                  borderRadius: 7,
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  color: '#475569',
                  fontWeight: 600,
                  fontSize: 13.5,
                  cursor: 'pointer',
                }}
              >
                Hủy bỏ
              </button>
              <button
                type="button"
                onClick={handleConfirmBulkDelete}
                disabled={bulkDeleting || !isBulkDeleteConfirmed}
                style={{
                  padding: '10px 20px',
                  borderRadius: 7,
                  border: 'none',
                  background: isBulkDeleteConfirmed ? '#dc2626' : '#94a3b8',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 13.5,
                  cursor: isBulkDeleteConfirmed && !bulkDeleting ? 'pointer' : 'not-allowed',
                  opacity: isBulkDeleteConfirmed ? 1 : 0.6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'all 0.15s ease',
                }}
              >
                <TrashIcon style={{ width: 16, height: 16 }} />
                <span>
                  {bulkDeleting
                    ? 'Đang xóa...'
                    : isBulkDeleteConfirmed
                      ? `Xác nhận xóa ${accountsToDelete.length} tài khoản`
                      : 'Gõ chữ OK để mở khóa'}
                </span>
              </button>
            </div>
          </section>
        </div>
      )}

      {/* 4. ACCOUNT DETAIL SLIDE-OVER DRAWER */}
      {detailDrawerAccount && (
        <div
          className="drawer-backdrop"
          onClick={() => setDetailDrawerAccount(null)}
        >
          <div
            className="drawer-panel"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="drawer-header">
              <div className="drawer-header-left">
                <div className="drawer-avatar-wrap">
                  <img
                    alt=""
                    src={buildDiceBearAvatarUrl(detailDrawerAccount)}
                    className="drawer-avatar-img"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
                <div className="drawer-header-info">
                  <h3>{detailDrawerAccount.full_name || detailDrawerAccount.email}</h3>
                  <span className="drawer-subtitle">{detailDrawerAccount.email}</span>
                  <div className="drawer-badges-row">
                    <span className={`drawer-role-badge ${detailDrawerAccount.role}`}>
                      {roleLabel(detailDrawerAccount.role)}
                    </span>
                    <span className={`drawer-status-badge ${getStatus(detailDrawerAccount)}`}>
                      {statusLabel(detailDrawerAccount)}
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDetailDrawerAccount(null)}
                className="drawer-close-btn"
                aria-label="Đóng"
              >
                <XMarkIcon className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="drawer-content">
              {/* SECTION 1: HẠN MỨC & LƯỢT THI */}
              {detailDrawerAccount.role === 'student' && (
                <div className="drawer-card">
                  <div className="drawer-card-title" style={{ justifyContent: 'space-between' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <SparklesIcon className="w-4 h-4 text-amber-500" />
                      <span>Hạn mức & Lượt thi</span>
                    </span>
                    <button
                      type="button"
                      className="icon-action-btn btn-plus-quota"
                      onClick={() => openQuotaModal(detailDrawerAccount)}
                      style={{ height: '26px', padding: '0 8px', fontSize: '11.5px' }}
                    >
                      <PlusIcon className="action-svg-icon" style={{ width: 13, height: 13 }} />
                      <span className="action-btn-label">Cấp thêm lượt</span>
                    </button>
                  </div>
                  <div className="drawer-quota-stats">
                    <div className="quota-metric-box">
                      <span className="metric-label">Đã thi</span>
                      <strong className="metric-val">{detailDrawerAccount.exam_used || 0}</strong>
                    </div>
                    <div className="quota-metric-box">
                      <span className="metric-label">Hạn mức</span>
                      <strong className="metric-val">
                        {detailDrawerAccount.exam_quota === 0 || detailDrawerAccount.exam_quota == null
                          ? 'Vô hạn'
                          : detailDrawerAccount.exam_quota}
                      </strong>
                    </div>
                    <div className="quota-metric-box">
                      <span className="metric-label">Luyện tập</span>
                      <strong className="metric-val text-purple-600">{detailDrawerAccount.practice_used || 0}</strong>
                    </div>
                  </div>
                  <div className="drawer-quota-bar">
                    <div
                      className="drawer-quota-fill"
                      style={{
                        width: `${
                          detailDrawerAccount.exam_quota === 0 || detailDrawerAccount.exam_quota == null
                            ? 100
                            : Math.min(100, Math.round(((detailDrawerAccount.exam_used || 0) * 100) / Math.max(1, detailDrawerAccount.exam_quota)))
                        }%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {/* SECTION 2: THỜI HẠN SỬ DỤNG */}
              <div className="drawer-card">
                <div className="drawer-card-title">
                  <CalendarDaysIcon className="w-4 h-4 text-blue-500" />
                  <span>Thời hạn sử dụng</span>
                </div>
                <div className="drawer-key-value-list">
                  <div className="drawer-kv-row">
                    <span className="kv-label">Ngày bắt đầu</span>
                    <strong className="kv-val">{formatDate(detailDrawerAccount.starts_at) || 'Từ khi tạo'}</strong>
                  </div>
                  <div className="drawer-kv-row">
                    <span className="kv-label">Ngày hết hạn</span>
                    <strong className="kv-val">{formatDate(detailDrawerAccount.expires_at)}</strong>
                  </div>
                  <div className="drawer-kv-row">
                    <span className="kv-label">Thời gian còn lại</span>
                    <span className={`expiry-countdown-pill ${getDaysLeftClass(detailDrawerAccount.expires_at)}`}>
                      {daysLeft(detailDrawerAccount.expires_at) !== null
                        ? daysLeft(detailDrawerAccount.expires_at) < 0
                          ? 'Đã hết hạn'
                          : `Còn ${daysLeft(detailDrawerAccount.expires_at)} ngày`
                        : 'Vô hạn'}
                    </span>
                  </div>
                  {detailDrawerAccount.center_name && (
                    <div className="drawer-kv-row">
                      <span className="kv-label">Trung tâm</span>
                      <strong className="kv-val">{detailDrawerAccount.center_name}</strong>
                    </div>
                  )}
                  {detailDrawerAccount.classroom_name && (
                    <div className="drawer-kv-row">
                      <span className="kv-label">Lớp học</span>
                      <strong className="kv-val">{detailDrawerAccount.classroom_name}</strong>
                    </div>
                  )}
                </div>
              </div>

              {/* SECTION 3: THÔNG TIN CÁ NHÂN & LIÊN HỆ */}
              <div className="drawer-card">
                <div className="drawer-card-title">
                  <UserIcon className="w-4 h-4 text-emerald-500" />
                  <span>Thông tin liên hệ & Hồ sơ</span>
                </div>
                <div className="drawer-key-value-list">
                  <div className="drawer-kv-row">
                    <span className="kv-label">Số điện thoại</span>
                    <strong className="kv-val">
                      {detailDrawerAccount.phone || detailDrawerAccount.phone_number || 'Chưa cập nhật'}
                    </strong>
                  </div>
                  <div className="drawer-kv-row">
                    <span className="kv-label">Email cá nhân</span>
                    <strong className="kv-val">
                      {detailDrawerAccount.personal_email || 'Chưa cập nhật'}
                    </strong>
                  </div>
                  <div className="drawer-kv-row">
                    <span className="kv-label">Kích hoạt lần đầu</span>
                    <span className={`activation-pill-tag ${detailDrawerAccount.first_login_completed ? 'activated' : 'pending'}`}>
                      {detailDrawerAccount.first_login_completed ? 'Đã kích hoạt' : 'Chưa đăng nhập'}
                    </span>
                  </div>
                  {detailDrawerAccount.last_login_at && (
                    <div className="drawer-kv-row">
                      <span className="kv-label">Đăng nhập gần nhất</span>
                      <span className="kv-val">{formatDateTime(detailDrawerAccount.last_login_at)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* SECTION 4: THÔNG TIN BẢO MẬT */}
              <div className="drawer-card">
                <div className="drawer-card-title">
                  <ShieldCheckIcon className="w-4 h-4 text-purple-500" />
                  <span>Mật khẩu ban đầu</span>
                </div>
                <div className="drawer-password-box">
                  <code>{detailDrawerAccount.initial_password || detailDrawerAccount.password || '(Đã bảo mật)'}</code>
                  {(detailDrawerAccount.initial_password || detailDrawerAccount.password) && (
                    <button
                      type="button"
                      className="drawer-copy-pwd-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(detailDrawerAccount.initial_password || detailDrawerAccount.password);
                        flashNotice('✓ Đã sao chép mật khẩu!');
                      }}
                    >
                      Sao chép
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="drawer-footer">
              <button
                type="button"
                className="drawer-action-btn primary"
                onClick={() => {
                  handleOpenEditModal(detailDrawerAccount);
                }}
              >
                <PencilSquareIcon className="w-4 h-4" />
                <span>Sửa thông tin</span>
              </button>
              {detailDrawerAccount.role === 'student' && (
                <button
                  type="button"
                  className="drawer-action-btn quota"
                  onClick={() => {
                    setQuotaModalAccount(detailDrawerAccount);
                    setQuotaAddValue(5);
                    setQuotaSetValue('');
                  }}
                >
                  <PlusIcon className="w-4 h-4" />
                  <span>+ Lượt thi</span>
                </button>
              )}
              <button
                type="button"
                className="drawer-action-btn extend"
                onClick={() => {
                  setExtendModalAccount(detailDrawerAccount);
                  setExtendDateValue(detailDrawerAccount.expires_at ? detailDrawerAccount.expires_at.split('T')[0] : 'unlimited');
                }}
              >
                <CalendarDaysIcon className="w-4 h-4" />
                <span>Gia hạn</span>
              </button>
              <button
                type="button"
                className="drawer-action-btn lock"
                onClick={() => handleLockToggle(detailDrawerAccount)}
              >
                {detailDrawerAccount.account_status === 'locked' ? (
                  <>
                    <LockOpenIcon className="w-4 h-4" />
                    <span>Mở khóa</span>
                  </>
                ) : (
                  <>
                    <LockClosedIcon className="w-4 h-4" />
                    <span>Khóa</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 5. EDIT ACCOUNT MODAL */}
      {editModalAccount && (
        <div
          className="account-modal-backdrop"
          role="presentation"
          onClick={() => !savingEdit && setEditModalAccount(null)}
        >
          <section
            className="account-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 560 }}
          >
            <div className="modal-result-header" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: '#eff6ff',
                    color: '#2563eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <PencilSquareIcon style={{ width: 22, height: 22 }} />
                </div>
                <div>
                  <h3 style={{ color: '#0f172a', margin: 0, fontSize: 18 }}>Chỉnh sửa thông tin tài khoản</h3>
                  <p style={{ margin: '3px 0 0', fontSize: 13, color: '#64748b' }}>
                    {editModalAccount.email} • {roleLabel(editModalAccount.role)}
                  </p>
                </div>
              </div>
              <button
                className="modal-close-icon"
                onClick={() => !savingEdit && setEditModalAccount(null)}
                aria-label="Đóng"
                type="button"
                disabled={savingEdit}
              >
                <XMarkIcon />
              </button>
            </div>

            <form onSubmit={handleSaveEditAccount} style={{ padding: '16px 0 0' }}>
              <div className="edit-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div className="form-group-block" style={{ gridColumn: 'span 2' }}>
                  <label>Họ và tên *</label>
                  <input
                    type="text"
                    required
                    value={editForm.full_name}
                    onChange={(e) => setEditForm((p) => ({ ...p, full_name: e.target.value }))}
                    placeholder="Nguyễn Văn A"
                    className="modern-input-field"
                  />
                </div>

                <div className="form-group-block">
                  <label>Số điện thoại</label>
                  <input
                    type="text"
                    value={editForm.phone}
                    onChange={(e) => setEditForm((p) => ({ ...p, phone: e.target.value }))}
                    placeholder="0912345678"
                    className="modern-input-field"
                  />
                </div>

                <div className="form-group-block">
                  <label>Email cá nhân</label>
                  <input
                    type="email"
                    value={editForm.personal_email}
                    onChange={(e) => setEditForm((p) => ({ ...p, personal_email: e.target.value }))}
                    placeholder="hocvien@gmail.com"
                    className="modern-input-field"
                  />
                </div>

                {editModalAccount.role === 'student' && (
                  <div className="form-group-block">
                    <label>Hạn mức lượt thi</label>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={editForm.is_unlimited_quota}
                          onChange={(e) => setEditForm((p) => ({ ...p, is_unlimited_quota: e.target.checked }))}
                        />
                        <span>Vô hạn</span>
                      </label>
                    </div>
                    {!editForm.is_unlimited_quota && (
                      <input
                        type="number"
                        min="0"
                        value={editForm.exam_quota}
                        onChange={(e) => setEditForm((p) => ({ ...p, exam_quota: e.target.value }))}
                        className="modern-input-field"
                      />
                    )}
                  </div>
                )}

                <div className="form-group-block">
                  <label>Hạn sử dụng</label>
                  <div className="chip-presets-row grid-4" style={{ marginBottom: 6 }}>
                    <button
                      type="button"
                      className={`modern-preset-chip ${editForm.expires_date === getTodayPlusMonths(1) ? 'active' : ''}`}
                      onClick={() => setEditForm((p) => ({ ...p, expires_date: getTodayPlusMonths(1) }))}
                    >
                      +1 tháng
                    </button>
                    <button
                      type="button"
                      className={`modern-preset-chip ${editForm.expires_date === getTodayPlusMonths(3) ? 'active' : ''}`}
                      onClick={() => setEditForm((p) => ({ ...p, expires_date: getTodayPlusMonths(3) }))}
                    >
                      +3 tháng
                    </button>
                    <button
                      type="button"
                      className={`modern-preset-chip ${editForm.expires_date === getTodayPlusMonths(6) ? 'active' : ''}`}
                      onClick={() => setEditForm((p) => ({ ...p, expires_date: getTodayPlusMonths(6) }))}
                    >
                      +6 tháng
                    </button>
                    <button
                      type="button"
                      className={`modern-preset-chip ${editForm.expires_date === getTodayPlusMonths(12) ? 'active' : ''}`}
                      onClick={() => setEditForm((p) => ({ ...p, expires_date: getTodayPlusMonths(12) }))}
                    >
                      +1 năm
                    </button>
                  </div>
                  <input
                    type="date"
                    value={editForm.expires_date}
                    onChange={(e) => setEditForm((p) => ({ ...p, expires_date: e.target.value }))}
                    className="modern-input-field date-picker"
                  />
                </div>

                <div className="form-group-block">
                  <label>Trạng thái tài khoản</label>
                  <select
                    value={editForm.account_status}
                    onChange={(e) => setEditForm((p) => ({ ...p, account_status: e.target.value }))}
                    className="modern-select-field"
                  >
                    <option value="active">Đang hoạt động (Mở)</option>
                    <option value="locked">Tạm khóa tài khoản</option>
                  </select>
                </div>

                <div className="form-group-block" style={{ gridColumn: 'span 2' }}>
                  <label>Đặt mật khẩu mới (bỏ trống nếu giữ nguyên)</label>
                  <input
                    type="text"
                    value={editForm.new_password}
                    onChange={(e) => setEditForm((p) => ({ ...p, new_password: e.target.value }))}
                    placeholder="Nhập mật khẩu mới nếu muốn đổi..."
                    className="modern-input-field"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24, paddingTop: 14, borderTop: '1px solid #e2e8f0' }}>
                <button
                  type="button"
                  onClick={() => setEditModalAccount(null)}
                  disabled={savingEdit}
                  style={{
                    padding: '9px 18px',
                    borderRadius: 7,
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    color: '#475569',
                    fontWeight: 600,
                    fontSize: 13.5,
                    cursor: 'pointer',
                  }}
                >
                  Hủy bỏ
                </button>
                <button
                  type="submit"
                  disabled={savingEdit}
                  style={{
                    padding: '9px 22px',
                    borderRadius: 7,
                    border: 'none',
                    background: '#2563eb',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 13.5,
                    cursor: savingEdit ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <CheckIcon style={{ width: 16, height: 16 }} />
                  <span>{savingEdit ? 'Đang lưu...' : 'Lưu thay đổi'}</span>
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {/* 6. ACTIVITY AUDIT LOG MODAL */}
      {showAuditLogModal && (
        <div
          className="account-modal-backdrop"
          role="presentation"
          onClick={() => setShowAuditLogModal(false)}
        >
          <section
            className="account-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 720 }}
          >
            <div className="modal-result-header" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: '#f1f5f9',
                    color: '#334155',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <ClockIcon style={{ width: 22, height: 22 }} />
                </div>
                <div>
                  <h3 style={{ color: '#0f172a', margin: 0, fontSize: 18 }}>Nhật ký hoạt động & Sao lưu</h3>
                  <p style={{ margin: '3px 0 0', fontSize: 13, color: '#64748b' }}>
                    Lịch sử các thao tác quản trị tài khoản và sao lưu phục hồi dữ liệu
                  </p>
                </div>
              </div>
              <button
                className="modal-close-icon"
                onClick={() => setShowAuditLogModal(false)}
                aria-label="Đóng"
                type="button"
              >
                <XMarkIcon />
              </button>
            </div>

            <div style={{ padding: '16px 0 10px', maxHeight: 420, overflowY: 'auto' }}>
              {loadingAuditLogs ? (
                <div style={{ textAlign: 'center', padding: '30px 0', color: '#64748b' }}>
                  <ArrowPathIcon className="w-6 h-6 animate-spin" style={{ margin: '0 auto 8px', color: '#2563eb' }} />
                  <span>Đang tải nhật ký...</span>
                </div>
              ) : auditLogs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px 0', color: '#64748b' }}>
                  <ShieldCheckIcon className="w-8 h-8 text-emerald-500" style={{ margin: '0 auto 8px' }} />
                  <p style={{ margin: 0, fontWeight: 600 }}>Chưa có bản ghi thao tác nguy hiểm nào được ghi nhận.</p>
                  <small style={{ color: '#94a3b8' }}>Mọi thao tác xóa tài khoản hoặc biến động sẽ được sao lưu an toàn tại đây.</small>
                </div>
              ) : (
                <div className="audit-log-list" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {auditLogs.map((log, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '12px 14px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, background: '#fee2e2', color: '#b91c1c', padding: '2px 8px', borderRadius: 4 }}>
                          Xóa tài khoản
                        </span>
                        <span style={{ fontSize: 12, color: '#64748b' }}>{formatDateTime(log.deleted_at)}</span>
                        <span style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>bởi {log.deleted_by || 'Admin'}</span>
                      </div>
                      <div style={{ fontSize: 13, color: '#1e293b' }}>
                        <strong>{log.full_name || log.email}</strong> • <code>{log.email}</code>
                        <span style={{ marginLeft: 8, fontSize: 11, background: '#e0f2fe', color: '#0369a1', padding: '1px 6px', borderRadius: 4 }}>
                          {roleLabel(log.role)}
                        </span>
                        {log.exam_quota && <span style={{ marginLeft: 6, fontSize: 12, color: '#64748b' }}>Quota: {log.exam_quota} lượt</span>}
                        {log.initial_password && <span style={{ marginLeft: 6, fontSize: 12, color: '#047857' }}>• MK: {log.initial_password}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 14, borderTop: '1px solid #e2e8f0' }}>
              <button
                type="button"
                onClick={() => setShowAuditLogModal(false)}
                style={{
                  padding: '9px 20px',
                  borderRadius: 7,
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  color: '#475569',
                  fontWeight: 600,
                  fontSize: 13.5,
                  cursor: 'pointer',
                }}
              >
                Đóng
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default AccountManagement;
